import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, CssBaseline,
  Avatar, Menu, MenuItem, Badge, Container, Grid, Card, CardContent,
  Paper, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Collapse, FormControlLabel, Switch, InputAdornment,
  Breadcrumbs, Link as MuiLink, Tooltip, useTheme, useMediaQuery, Fab,
  TablePagination, FormControl, InputLabel, Select, CircularProgress,
  Autocomplete, Radio, RadioGroup, FormLabel, Checkbox
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Business, People, LocalOffer, Event,
  Assessment, Payment, Settings, Notifications, Search, AccountCircle,
  ChevronLeft, ChevronRight, Add, Edit, Delete, Block, CheckCircle,
  TrendingUp, AttachMoney, EventSeat, PersonAdd, MoreVert, Logout,
  School, BarChart, Receipt, Support, AdminPanelSettings,
  CalendarToday, Groups, LocationOn, Refresh, PieChart, ShowChart,
  LocalFireDepartment, WatchLater, Insights, AutoGraph, Language,
  TrendingDown, Star, AccessTime, Campaign, Store, Person,
  ExpandMore, ExpandLess, Sell, LockReset, PhotoLibrary, CloudUpload, Phone, QuestionAnswer
} from '@mui/icons-material';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import api from '../utils/api';
import ThemeToggle from '../components/ThemeToggle';
import { INDIAN_CITIES } from '../utils/indianCities';
import { AuthContext } from '../context/AuthContext';
import StudentFormDialog from './librarian/StudentFormDialog';
import MarketingUsersManagement from '../components/admin/MarketingUsersManagement';
import SalesUsersManagement from '../components/admin/SalesUsersManagement';
import SeoUsersManagement from '../components/admin/SeoUsersManagement';
import InstituteLeadsManagement from '../components/admin/InstituteLeadsManagement';
import WithdrawalManagement from '../components/admin/WithdrawalManagement';
import FinancePanel from '../components/admin/FinancePanel';
import AnalyticsPage from './admin/analytics/AnalyticsPage';
import {
  GlassCard,
  GlassPaper,
  GradientButton,
  StatCard,
  SectionHeader,
  GlassTable,
  GlassDialog,
  ShimmerTableRows,
  EmptyState,
  glass,
  ADMIN_GRADIENTS,
  ADMIN_COLORS,
} from '../components/admin/ui';

const drawerWidth = 280;

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

function AdminDashboardPage() {
  // Auth context
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // Layout state
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [currentView, setCurrentView] = useState('dashboard');

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [libraryMenuAnchor, setLibraryMenuAnchor] = useState(null);

  // Data state
  const [analytics, setAnalytics] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [students, setStudents] = useState([]);
  const [librarians, setLibrarians] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Library search, filter, pagination state
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryStatus, setLibraryStatus] = useState('');
  const [libraryPage, setLibraryPage] = useState(0);
  const [libraryRowsPerPage, setLibraryRowsPerPage] = useState(10);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [libraryLoading, setLibraryLoading] = useState(false);

  // Student search, filter, pagination state
  const [studentSearch, setStudentSearch] = useState('');
  const [studentStatus, setStudentStatus] = useState('');
  const [studentCity, setStudentCity] = useState('');
  const [studentOrigin, setStudentOrigin] = useState('');
  const [studentPage, setStudentPage] = useState(0);
  const [studentRowsPerPage, setStudentRowsPerPage] = useState(20);
  const [studentTotal, setStudentTotal] = useState(0);
  const [studentCities, setStudentCities] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [createStudentOpen, setCreateStudentOpen] = useState(false);
  const [editStudentOpen, setEditStudentOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Bookings state
  const [bookings, setBookings] = useState([]);
  const [bookingsStats, setBookingsStats] = useState({});
  const [bookingsFilters, setBookingsFilters] = useState({ libraries: [] });
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingStatus, setBookingStatus] = useState('');
  const [bookingPaymentStatus, setBookingPaymentStatus] = useState('');
  const [bookingLibrary, setBookingLibrary] = useState('');
  const [bookingStartDate, setBookingStartDate] = useState('');
  const [bookingEndDate, setBookingEndDate] = useState('');
  const [bookingPage, setBookingPage] = useState(0);
  const [bookingRowsPerPage, setBookingRowsPerPage] = useState(20);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);
  const [notificationLoading, setNotificationLoading] = useState(false);

  // Library search for assignment dropdown
  const [librarySearchOptions, setLibrarySearchOptions] = useState([]);
  const [librarySearchLoading, setLibrarySearchLoading] = useState(false);
  const [selectedAssignLibrary, setSelectedAssignLibrary] = useState(null);

  // Dialog states
  const [createLibraryOpen, setCreateLibraryOpen] = useState(false);
  const [editLibraryOpen, setEditLibraryOpen] = useState(false);
  const [deleteLibraryOpen, setDeleteLibraryOpen] = useState(false);
  const [managePlansOpen, setManagePlansOpen] = useState(false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [deletePlanOpen, setDeletePlanOpen] = useState(false);
  const [manageImagesOpen, setManageImagesOpen] = useState(false);

  // Library Images state
  const [libraryImages, setLibraryImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Data states
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [libraryPlans, setLibraryPlans] = useState([]);
  const [newLibrary, setNewLibrary] = useState({
    name: '',
    address: '',
    capacity: '',
    city: null,
    website_enabled: false,
    librarianMode: 'none', // 'none', 'existing', 'new'
    existing_librarian_id: null,
    librarian_name: '',
    librarian_email: '',
    librarian_password: '',
    librarian_phone: ''
  });
  const [editLibraryData, setEditLibraryData] = useState({ name: '', address: '', capacity: '', city: null, lat: '', lng: '', website_enabled: false, amenities: [] });
  const [newPlan, setNewPlan] = useState({ plan_type: '', price_cents: '', recurring: false, hours_per_day: null, first_time_discount: null });
  const [editPlanData, setEditPlanData] = useState({ price_cents: '', recurring: false, hours_per_day: null, first_time_discount: null });

  // Plan type configurations
  const PLAN_TYPES = {
    daily: { label: 'Daily', duration: '1 Day', description: '1 Day access to the library' },
    weekly: { label: 'Weekly', duration: '7 Days', description: '7 Days access to the library' },
    monthly: { label: 'Monthly', duration: '1 Month', description: '1 Month (30 days) access to the library' },
    half_yearly: { label: 'Half-Yearly', duration: '6 Months', description: '6 Months (180 days) access to the library' },
  };

  const HOURS_OPTIONS = [
    { value: null, label: 'Unlimited' },
    { value: 2, label: '2 hours/day' },
    { value: 4, label: '4 hours/day' },
    { value: 6, label: '6 hours/day' },
    { value: 8, label: '8 hours/day' },
    { value: 10, label: '10 hours/day' },
    { value: 12, label: '12 hours/day' },
    { value: 14, label: '14 hours/day' },
    { value: 16, label: '16 hours/day' },
    { value: 18, label: '18 hours/day' },
    { value: 20, label: '20 hours/day' },
    { value: 22, label: '22 hours/day' },
    { value: 24, label: '24 hours/day' },
  ];

  const DISCOUNT_OPTIONS = [
    { value: 10, label: '10% off' },
    { value: 20, label: '20% off' },
    { value: 30, label: '30% off' },
    { value: 40, label: '40% off' },
    { value: 50, label: '50% off' },
  ];

  const AMENITIES_OPTIONS = [
    'WiFi', 'Air Conditioning', 'Parking', 'Cafeteria', 'Water Dispenser',
    'Locker Facility', 'Power Backup', 'CCTV', '24/7 Access', 'Study Rooms',
    'Discussion Rooms', 'Printing Services', 'Library', 'Whiteboard',
    'Bean Bags', 'Ergonomic Chairs', 'Natural Light', 'Silent Zone',
    'Charging Points', 'Stationery Store',
  ];

  // Librarian states
  const [createLibrarianOpen, setCreateLibrarianOpen] = useState(false);
  const [editLibrarianOpen, setEditLibrarianOpen] = useState(false);
  const [assignLibrarianOpen, setAssignLibrarianOpen] = useState(false);
  const [selectedLibrarian, setSelectedLibrarian] = useState(null);
  const [newLibrarian, setNewLibrarian] = useState({ name: '', email: '', password: '', phone: '', library_id: '' });
  const [editLibrarianData, setEditLibrarianData] = useState({ name: '', email: '', phone: '', city: '' });

  // Librarian search, filter, pagination state
  const [librarianSearch, setLibrarianSearch] = useState('');
  const [librarianStatus, setLibrarianStatus] = useState('');
  const [librarianPage, setLibrarianPage] = useState(0);
  const [librarianRowsPerPage, setLibrarianRowsPerPage] = useState(20);
  const [librarianTotal, setLibrarianTotal] = useState(0);
  const [librarianLoading, setLibrarianLoading] = useState(false);

  // Unassigned librarians for library creation
  const [unassignedLibrarians, setUnassignedLibrarians] = useState([]);
  const [unassignedLibrariansLoading, setUnassignedLibrariansLoading] = useState(false);

  // Institute states
  const [institutes, setInstitutes] = useState([]);
  const [createInstituteOpen, setCreateInstituteOpen] = useState(false);

  // Institute search, filter, pagination state
  const [instituteSearch, setInstituteSearch] = useState('');
  const [instituteStatus, setInstituteStatus] = useState('');
  const [instituteCity, setInstituteCity] = useState('');
  const [institutePage, setInstitutePage] = useState(0);
  const [instituteRowsPerPage, setInstituteRowsPerPage] = useState(10);
  const [instituteTotal, setInstituteTotal] = useState(0);
  const [instituteLoading, setInstituteLoading] = useState(false);
  const [editInstituteOpen, setEditInstituteOpen] = useState(false);
  const [deleteInstituteOpen, setDeleteInstituteOpen] = useState(false);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [newInstitute, setNewInstitute] = useState({
    name: '',
    description: '',
    address: '',
    city: null,
    contact_email: '',
    contact_phone: '',
    website: '',
    courses_offered: [],
    facilities: [],
    manager_email: '',
    manager_name: '',
    manager_password: ''
  });
  const [editInstituteData, setEditInstituteData] = useState({
    name: '',
    description: '',
    address: '',
    city: null,
    lat: '',
    lng: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    courses_offered: [],
    facilities: []
  });

  // Institute Manager states
  const [instituteManagers, setInstituteManagers] = useState([]);
  const [createInstituteManagerOpen, setCreateInstituteManagerOpen] = useState(false);
  const [assignInstituteManagerOpen, setAssignInstituteManagerOpen] = useState(false);
  const [selectedInstituteManager, setSelectedInstituteManager] = useState(null);
  const [newInstituteManager, setNewInstituteManager] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    city: '',
    institute_id: ''
  });

  // Institute Manager search, filter, pagination state
  const [managerSearch, setManagerSearch] = useState('');
  const [managerStatus, setManagerStatus] = useState('');
  const [managerPage, setManagerPage] = useState(0);
  const [managerRowsPerPage, setManagerRowsPerPage] = useState(20);
  const [managerTotal, setManagerTotal] = useState(0);
  const [managerLoading, setManagerLoading] = useState(false);

  // Password reset states
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Sidebar menu items
  const menuSections = [
    {
      id: 'main',
      items: [
        { id: 'dashboard', text: 'Dashboard', icon: <Dashboard />, implemented: true },
      ]
    },
    {
      id: 'library-section',
      label: 'Library Management',
      icon: <Business />,
      items: [
        { id: 'libraries', text: 'Libraries', icon: <Business />, implemented: true },
        { id: 'librarians', text: 'Librarians', icon: <Person />, implemented: true },
      ]
    },
    {
      id: 'institute-section',
      label: 'Institute Management',
      icon: <School />,
      items: [
        { id: 'institutes', text: 'Institutes', icon: <School />, implemented: true },
        { id: 'institute-managers', text: 'Managers', icon: <AdminPanelSettings />, implemented: true },
        { id: 'institute-leads', text: 'Student Inquiries', icon: <QuestionAnswer />, implemented: true },
      ]
    },
    {
      id: 'booking-section',
      label: 'Bookings & Sessions',
      icon: <Event />,
      items: [
        { id: 'bookings', text: 'Bookings', icon: <Event />, implemented: true },
        { id: 'sessions', text: 'Active Sessions', icon: <Groups />, implemented: true },
      ]
    },
    {
      id: 'finance-section',
      label: 'Finance',
      icon: <AttachMoney />,
      items: [
        { id: 'finance', text: 'Overview', icon: <AttachMoney />, implemented: true },
        { id: 'withdrawals', text: 'Withdrawals', icon: <Payment />, implemented: true },
      ]
    },
    {
      id: 'marketing-section',
      label: 'Marketing & Analytics',
      icon: <Campaign />,
      items: [
        { id: 'marketing-users', text: 'Marketing Users', icon: <Campaign />, implemented: true },
        { id: 'analytics', text: 'Analytics', icon: <BarChart />, implemented: true },
      ]
    },
    {
      id: 'users-section',
      label: 'User Management',
      icon: <People />,
      items: [
        { id: 'students', text: 'Students', icon: <People />, implemented: true },
        { id: 'sales-users', text: 'Sales Users', icon: <Sell />, implemented: true },
        { id: 'seo-users', text: 'SEO Users', icon: <Language />, implemented: true },
      ]
    },
  ];

  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState({
    'library-section': true,
    'institute-section': true,
    'booking-section': true,
    'finance-section': true,
    'marketing-section': true,
    'users-section': true,
    'other-section': false,
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, librariesRes, institutesRes, studentsRes, librariansRes, instituteManagersRes, sessionsRes] = await Promise.all([
        api.get('/admin/analytics'),
        api.get('/libraries'),
        api.get('/admin/institutes').catch(() => ({ data: { data: { institutes: [] } } })),
        api.get('/admin/students'),
        api.get('/admin/librarians').catch(() => ({ data: { data: { librarians: [] } } })),
        api.get('/admin/institute-managers').catch(() => ({ data: { data: { managers: [] } } })),
        api.get('/admin/sessions/active').catch(() => ({ data: { data: { sessions: [] } } }))
      ]);
      setAnalytics(analyticsRes.data.data.analytics);
      setLibraries(librariesRes.data.data.libraries);
      setInstitutes(institutesRes.data.data.institutes || []);
      setStudents(studentsRes.data.data.students || []);
      setLibrarians(librariansRes.data.data.librarians || []);
      setInstituteManagers(instituteManagersRes.data.data.managers || []);
      setActiveSessions(sessionsRes.data.data.sessions || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      showSnackbar('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      const response = await api.get('/admin/notifications?limit=10');
      setNotifications(response.data.data.notifications || []);
      setNotificationUnreadCount(response.data.data.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setNotificationLoading(false);
    }
  };

  // Fetch notification unread count
  const fetchNotificationCount = async () => {
    try {
      const response = await api.get('/admin/notifications/unread-count');
      setNotificationUnreadCount(response.data.data.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching notification count:', err);
    }
  };

  // Mark notification as read and navigate
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      await api.put(`/admin/notifications/${notification.id}/read`);

      // Update local state
      setNotifications(prev => prev.map(n =>
        n.id === notification.id ? { ...n, is_read: true } : n
      ));
      setNotificationUnreadCount(prev => Math.max(0, prev - 1));

      // Close menu
      setNotificationMenuAnchor(null);

      // Navigate based on action_url
      if (notification.action_url) {
        // Parse the action_url to extract view parameter
        const urlParams = new URLSearchParams(notification.action_url.split('?')[1]);
        const view = urlParams.get('view');
        if (view) {
          setCurrentView(view);
        }
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Mark all notifications as read
  const handleMarkAllRead = async () => {
    try {
      await api.put('/admin/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setNotificationUnreadCount(0);
      showSnackbar('All notifications marked as read');
    } catch (err) {
      console.error('Error marking all as read:', err);
      showSnackbar('Failed to mark all as read', 'error');
    }
  };

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();
    // Refresh notification count every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch libraries with search, filter, and pagination
  const fetchLibraries = async () => {
    try {
      setLibraryLoading(true);
      const params = new URLSearchParams({
        page: libraryPage + 1,
        limit: libraryRowsPerPage,
      });
      if (librarySearch) params.append('search', librarySearch);
      if (libraryStatus) params.append('status', libraryStatus);

      const response = await api.get(`/admin/libraries?${params}`);
      setLibraries(response.data.data.libraries || []);
      setLibraryTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching libraries:', err);
      showSnackbar('Failed to load libraries', 'error');
    } finally {
      setLibraryLoading(false);
    }
  };

  // Fetch institutes with search, filter, and pagination
  const fetchInstitutes = async () => {
    try {
      setInstituteLoading(true);
      const params = new URLSearchParams({
        page: institutePage + 1,
        limit: instituteRowsPerPage,
      });
      if (instituteSearch) params.append('search', instituteSearch);
      if (instituteStatus) params.append('status', instituteStatus);
      if (instituteCity) params.append('city', instituteCity);

      const response = await api.get(`/admin/institutes?${params}`);
      setInstitutes(response.data.data.institutes || []);
      setInstituteTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching institutes:', err);
      showSnackbar('Failed to load institutes', 'error');
    } finally {
      setInstituteLoading(false);
    }
  };

  // Trigger library fetch when view is libraries or pagination changes
  useEffect(() => {
    if (currentView === 'libraries') {
      fetchLibraries();
    }
  }, [currentView, libraryPage, libraryRowsPerPage]);

  // Trigger institute fetch when view is institutes or pagination changes
  useEffect(() => {
    if (currentView === 'institutes') {
      fetchInstitutes();
    }
  }, [currentView, institutePage, instituteRowsPerPage]);

  // Fetch students with search, filter, and pagination
  const fetchStudents = async () => {
    try {
      setStudentLoading(true);
      const params = new URLSearchParams({
        page: studentPage + 1,
        limit: studentRowsPerPage,
      });
      if (studentSearch) params.append('search', studentSearch);
      if (studentStatus) params.append('status', studentStatus);
      if (studentCity) params.append('city', studentCity);
      if (studentOrigin) params.append('origin', studentOrigin);

      const response = await api.get(`/admin/students?${params}`);
      setStudents(response.data.data.students || []);
      setStudentTotal(response.data.data.pagination?.total || 0);
      if (response.data.data.filters?.cities) {
        setStudentCities(response.data.data.filters.cities);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      showSnackbar('Failed to load students', 'error');
    } finally {
      setStudentLoading(false);
    }
  };

  // Trigger student fetch when view is students or pagination changes
  useEffect(() => {
    if (currentView === 'students') {
      fetchStudents();
    }
  }, [currentView, studentPage, studentRowsPerPage]);

  // Fetch librarians with search, filter, and pagination
  const fetchLibrarians = async () => {
    try {
      setLibrarianLoading(true);
      const params = new URLSearchParams({
        page: librarianPage + 1,
        limit: librarianRowsPerPage,
      });
      if (librarianSearch) params.append('search', librarianSearch);
      if (librarianStatus) params.append('status', librarianStatus);

      const response = await api.get(`/admin/librarians?${params}`);
      setLibrarians(response.data.data.librarians || []);
      setLibrarianTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching librarians:', err);
      showSnackbar('Failed to load librarians', 'error');
    } finally {
      setLibrarianLoading(false);
    }
  };

  // Trigger librarian fetch when view is librarians or pagination changes
  useEffect(() => {
    if (currentView === 'librarians') {
      fetchLibrarians();
    }
  }, [currentView, librarianPage, librarianRowsPerPage]);

  // Fetch institute managers with search, filter, and pagination
  const fetchInstituteManagers = async () => {
    try {
      setManagerLoading(true);
      const params = new URLSearchParams({
        page: managerPage + 1,
        limit: managerRowsPerPage,
      });
      if (managerSearch) params.append('search', managerSearch);
      if (managerStatus) params.append('status', managerStatus);

      const response = await api.get(`/admin/institute-managers?${params}`);
      setInstituteManagers(response.data.data.managers || []);
      setManagerTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching institute managers:', err);
      showSnackbar('Failed to load institute managers', 'error');
    } finally {
      setManagerLoading(false);
    }
  };

  // Trigger institute manager fetch when view is institute-managers or pagination changes
  useEffect(() => {
    if (currentView === 'institute-managers') {
      fetchInstituteManagers();
    }
  }, [currentView, managerPage, managerRowsPerPage]);

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      setBookingLoading(true);
      const params = new URLSearchParams({
        page: bookingPage + 1,
        limit: bookingRowsPerPage,
      });
      if (bookingSearch) params.append('search', bookingSearch);
      if (bookingStatus) params.append('status', bookingStatus);
      if (bookingPaymentStatus) params.append('payment_status', bookingPaymentStatus);
      if (bookingLibrary) params.append('library_id', bookingLibrary);
      if (bookingStartDate) params.append('start_date', bookingStartDate);
      if (bookingEndDate) params.append('end_date', bookingEndDate);

      const response = await api.get(`/admin/bookings?${params}`);
      setBookings(response.data.data.bookings || []);
      setBookingTotal(response.data.data.pagination?.total || 0);
      setBookingsStats(response.data.data.stats || {});
      if (response.data.data.filters?.libraries) {
        setBookingsFilters({ libraries: response.data.data.filters.libraries });
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      showSnackbar('Failed to load bookings', 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  // Trigger bookings fetch when view is bookings or filters change
  useEffect(() => {
    if (currentView === 'bookings') {
      fetchBookings();
    }
  }, [currentView, bookingPage, bookingRowsPerPage, bookingStatus, bookingPaymentStatus, bookingLibrary]);

  // Search libraries for assignment dropdown
  const searchLibrariesForAssignment = async (searchText) => {
    try {
      setLibrarySearchLoading(true);
      const params = new URLSearchParams({ limit: 50 });
      if (searchText) params.append('search', searchText);

      const response = await api.get(`/admin/libraries?${params}`);
      setLibrarySearchOptions(response.data.data.libraries || []);
    } catch (err) {
      console.error('Error searching libraries:', err);
    } finally {
      setLibrarySearchLoading(false);
    }
  };

  // Load libraries when assign dialog opens
  useEffect(() => {
    if (assignLibrarianOpen) {
      searchLibrariesForAssignment('');
      // Pre-select current library if exists
      if (selectedLibrarian?.library) {
        setSelectedAssignLibrary(selectedLibrarian.library);
      } else {
        setSelectedAssignLibrary(null);
      }
    }
  }, [assignLibrarianOpen, selectedLibrarian]);

  const handleMenuClick = (itemId, implemented) => {
    if (!implemented) {
      showSnackbar('Coming Soon! This feature is under development.', 'info');
      return;
    }
    setCurrentView(itemId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Fetch unassigned librarians for library creation
  const fetchUnassignedLibrarians = async () => {
    try {
      setUnassignedLibrariansLoading(true);
      const response = await api.get('/admin/librarians');
      // Filter to only show librarians without a library assigned
      const unassigned = (response.data.data.librarians || []).filter(l => !l.library);
      setUnassignedLibrarians(unassigned);
    } catch (err) {
      console.error('Failed to fetch unassigned librarians', err);
    } finally {
      setUnassignedLibrariansLoading(false);
    }
  };

  const handleCreateLibrary = async () => {
    try {
      const payload = {
        name: newLibrary.name,
        address: newLibrary.address,
        capacity: parseInt(newLibrary.capacity, 10),
        city: newLibrary.city || null,
        website_enabled: newLibrary.website_enabled,
      };

      // Add librarian data based on mode
      if (newLibrary.librarianMode === 'existing' && newLibrary.existing_librarian_id) {
        payload.existing_librarian_id = newLibrary.existing_librarian_id;
      } else if (newLibrary.librarianMode === 'new') {
        if (!newLibrary.librarian_name || !newLibrary.librarian_email || !newLibrary.librarian_password) {
          showSnackbar('Please fill in all librarian details', 'error');
          return;
        }
        payload.librarian_name = newLibrary.librarian_name;
        payload.librarian_email = newLibrary.librarian_email;
        payload.librarian_password = newLibrary.librarian_password;
        if (newLibrary.librarian_phone) payload.librarian_phone = newLibrary.librarian_phone;
      }

      await api.post('/admin/libraries', payload);
      setCreateLibraryOpen(false);
      setNewLibrary({
        name: '',
        address: '',
        capacity: '',
        city: null,
        website_enabled: false,
        librarianMode: 'none',
        existing_librarian_id: null,
        librarian_name: '',
        librarian_email: '',
        librarian_password: '',
        librarian_phone: ''
      });
      showSnackbar('Library created successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create library', 'error');
    }
  };

  const handleEditLibrary = async () => {
    try {
      await api.put(`/admin/libraries/${selectedLibrary.id}`, {
        name: editLibraryData.name,
        address: editLibraryData.address,
        capacity: parseInt(editLibraryData.capacity, 10),
        city: editLibraryData.city || null,
        lat: editLibraryData.lat ? parseFloat(editLibraryData.lat) : null,
        lng: editLibraryData.lng ? parseFloat(editLibraryData.lng) : null,
        website_enabled: editLibraryData.website_enabled,
        amenities: editLibraryData.amenities,
      });
      setEditLibraryOpen(false);
      setSelectedLibrary(null);
      showSnackbar('Library updated successfully');
      fetchLibraries();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update library', 'error');
    }
  };

  const handleDeleteLibrary = async () => {
    try {
      await api.delete(`/admin/libraries/${selectedLibrary.id}`);
      showSnackbar('Library deleted successfully');
      setDeleteLibraryOpen(false);
      setSelectedLibrary(null);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete library', 'error');
    }
  };

  const handleToggleLibraryStatus = async (libraryId, currentStatus) => {
    try {
      await api.put(`/admin/libraries/${libraryId}/status`, { active: !currentStatus });
      showSnackbar(`Library ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchLibraries();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update library status', 'error');
    }
  };

  const handleToggleTopRated = async (libraryId, currentTopRated) => {
    try {
      await api.put(`/admin/libraries/${libraryId}/top-rated`, { top_rated: !currentTopRated });
      showSnackbar(`Library ${!currentTopRated ? 'marked as' : 'removed from'} top rated`);
      fetchDashboardData();
      setLibraryMenuAnchor(null);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update top rated status', 'error');
    }
  };

  const handleBlockStudent = async (studentId, blocked) => {
    try {
      await api.put(`/admin/users/${studentId}/block`, { blocked });
      setStudents(students.map(s => s.id === studentId ? { ...s, blocked } : s));
      showSnackbar(`Student ${blocked ? 'blocked' : 'unblocked'} successfully`);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update student', 'error');
    }
  };

  const openManagePlans = async (library) => {
    try {
      setSelectedLibrary(library);
      const response = await api.get('/plans', { params: { library_id: library.id } });
      setLibraryPlans(response.data.data.plans || []);
      setManagePlansOpen(true);
    } catch (err) {
      showSnackbar('Failed to load plans', 'error');
    }
  };

  const handleCreatePlan = async () => {
    try {
      await api.post(`/admin/libraries/${selectedLibrary.id}/plans`, {
        plan_type: newPlan.plan_type,
        price_cents: parseInt(newPlan.price_cents, 10),
        recurring: newPlan.recurring,
        hours_per_day: newPlan.hours_per_day,
        first_time_discount: newPlan.first_time_discount,
      });
      setCreatePlanOpen(false);
      setNewPlan({ plan_type: '', price_cents: '', recurring: false, hours_per_day: null, first_time_discount: null });
      showSnackbar('Plan created successfully');
      openManagePlans(selectedLibrary);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create plan', 'error');
    }
  };

  const handleEditPlan = async () => {
    try {
      await api.put(`/admin/plans/${selectedPlan.id}`, {
        price_cents: parseInt(editPlanData.price_cents, 10),
        recurring: editPlanData.recurring,
        hours_per_day: editPlanData.hours_per_day,
        first_time_discount: editPlanData.first_time_discount,
      });
      setEditPlanOpen(false);
      setSelectedPlan(null);
      showSnackbar('Plan updated successfully');
      openManagePlans(selectedLibrary);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update plan', 'error');
    }
  };

  const handleDeletePlan = async () => {
    try {
      await api.delete(`/admin/plans/${selectedPlan.id}`);
      showSnackbar('Plan deleted successfully');
      setDeletePlanOpen(false);
      setSelectedPlan(null);
      openManagePlans(selectedLibrary);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete plan', 'error');
    }
  };

  // Library Image handlers
  const openManageImages = async (library) => {
    try {
      setSelectedLibrary(library);
      setImagesLoading(true);
      setManageImagesOpen(true);
      const response = await api.get(`/admin/libraries/${library.id}/images`);
      setLibraryImages(response.data.data.gallery || []);
    } catch (err) {
      showSnackbar('Failed to load images', 'error');
      setLibraryImages([]);
    } finally {
      setImagesLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }

    try {
      const response = await api.post(`/admin/libraries/${selectedLibrary.id}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setLibraryImages(response.data.data.gallery || []);
      showSnackbar(`${files.length} image(s) uploaded successfully`);
      // Clear file input
      event.target.value = '';
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to upload images', 'error');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await api.delete(`/admin/libraries/${selectedLibrary.id}/images`, {
        data: { imageUrl },
      });
      setLibraryImages(response.data.data.gallery || []);
      showSnackbar('Image deleted successfully');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete image', 'error');
    }
  };

  // Librarian handlers
  const handleCreateLibrarian = async () => {
    try {
      if (!newLibrarian.name || !newLibrarian.email || !newLibrarian.password) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      await api.post('/admin/librarians', {
        name: newLibrarian.name,
        email: newLibrarian.email,
        password: newLibrarian.password,
        phone: newLibrarian.phone || null,
        library_id: newLibrarian.library_id || null
      });

      showSnackbar('Librarian created successfully');
      setCreateLibrarianOpen(false);
      setNewLibrarian({ name: '', email: '', password: '', phone: '', library_id: '' });
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create librarian', 'error');
    }
  };

  const handleAssignLibrarian = async (librarianId, libraryId) => {
    try {
      await api.put(`/admin/librarians/${librarianId}/assign`, {
        library_id: libraryId || null
      });

      showSnackbar(libraryId ? 'Librarian assigned successfully' : 'Librarian unassigned successfully');
      setAssignLibrarianOpen(false);
      setSelectedLibrarian(null);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update librarian', 'error');
    }
  };

  const handleDeleteLibrarian = async (librarianId) => {
    try {
      await api.delete(`/admin/librarians/${librarianId}`);
      showSnackbar('Librarian deleted successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete librarian', 'error');
    }
  };

  const handleUpdateLibrarian = async () => {
    if (!selectedLibrarian) return;

    try {
      await api.put(`/admin/librarians/${selectedLibrarian.id}`, {
        name: editLibrarianData.name,
        phone: editLibrarianData.phone || null,
        city: editLibrarianData.city || null,
      });
      showSnackbar('Librarian updated successfully');
      setEditLibrarianOpen(false);
      setSelectedLibrarian(null);
      fetchLibrarians();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update librarian', 'error');
    }
  };

  const openAssignLibrarianDialog = (librarian) => {
    setSelectedLibrarian(librarian);
    setAssignLibrarianOpen(true);
  };

  // Password Reset Handlers
  const openResetPasswordDialog = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const handleResetPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        showSnackbar('Password must be at least 6 characters', 'error');
        return;
      }

      setResetPasswordLoading(true);
      await api.post(`/admin/users/${resetPasswordUser.id}/reset-password`, {
        new_password: newPassword
      });

      showSnackbar(`Password reset successfully for ${resetPasswordUser.name}`);
      setResetPasswordOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Institute Management Handlers
  const handleCreateInstitute = async () => {
    try {
      if (!newInstitute.name || !newInstitute.address) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      const payload = {
        name: newInstitute.name,
        address: newInstitute.address,
        city: newInstitute.city || null,
        courses_offered: newInstitute.courses_offered || [],
        facilities: newInstitute.facilities || [],
        contact_email: newInstitute.contact_email || null,
        established_year: newInstitute.established_year ? parseInt(newInstitute.established_year, 10) : null,
      };

      // If manager info provided, include it for one-step creation
      if (newInstitute.manager_email && newInstitute.manager_name && newInstitute.manager_password) {
        payload.manager_email = newInstitute.manager_email;
        payload.manager_name = newInstitute.manager_name;
        payload.manager_password = newInstitute.manager_password;
        payload.manager_phone = newInstitute.manager_phone || null;
      }

      await api.post('/admin/institutes', payload);
      setCreateInstituteOpen(false);
      setNewInstitute({
        name: '', description: '', address: '', city: null,
        courses_offered: [], facilities: [],
        contact_email: '', established_year: '',
        manager_email: '', manager_name: '', manager_password: '', manager_phone: ''
      });
      showSnackbar('Institute created successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create institute', 'error');
    }
  };

  const handleEditInstitute = async () => {
    try {
      const payload = {
        name: editInstituteData.name,
        address: editInstituteData.address,
        city: editInstituteData.city || null,
        lat: editInstituteData.lat ? parseFloat(editInstituteData.lat) : null,
        lng: editInstituteData.lng ? parseFloat(editInstituteData.lng) : null,
        courses_offered: editInstituteData.courses_offered || [],
        facilities: editInstituteData.facilities || [],
        contact_email: editInstituteData.contact_email || null,
        established_year: editInstituteData.established_year ? parseInt(editInstituteData.established_year, 10) : null,
      };

      await api.put(`/admin/institutes/${selectedInstitute.id}`, payload);
      setEditInstituteOpen(false);
      setSelectedInstitute(null);
      showSnackbar('Institute updated successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update institute', 'error');
    }
  };

  const handleDeleteInstitute = async () => {
    try {
      await api.delete(`/admin/institutes/${selectedInstitute.id}`);
      showSnackbar('Institute deleted successfully');
      setDeleteInstituteOpen(false);
      setSelectedInstitute(null);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete institute', 'error');
    }
  };

  const handleToggleInstituteStatus = async (instituteId, currentStatus) => {
    try {
      await api.put(`/admin/institutes/${instituteId}/status`, { active: !currentStatus });
      showSnackbar(`Institute ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update institute status', 'error');
    }
  };

  const openEditInstituteDialog = (institute) => {
    setSelectedInstitute(institute);
    setEditInstituteData({
      name: institute.name,
      address: institute.address,
      city: institute.city || null,
      lat: institute.lat || '',
      lng: institute.lng || '',
      courses_offered: institute.courses_offered || [],
      facilities: institute.facilities || [],
      contact_email: institute.contact_email || '',
      established_year: institute.established_year || '',
    });
    setEditInstituteOpen(true);
  };

  const openDeleteInstituteDialog = (institute) => {
    setSelectedInstitute(institute);
    setDeleteInstituteOpen(true);
  };

  // Institute Manager Handlers
  const handleCreateInstituteManager = async () => {
    try {
      if (!newInstituteManager.name || !newInstituteManager.email || !newInstituteManager.password) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      await api.post('/admin/institute-managers', {
        name: newInstituteManager.name,
        email: newInstituteManager.email,
        password: newInstituteManager.password,
        phone: newInstituteManager.phone || null,
        city: newInstituteManager.city || null,
        institute_id: newInstituteManager.institute_id || null
      });

      showSnackbar('Institute manager created successfully');
      setCreateInstituteManagerOpen(false);
      setNewInstituteManager({ name: '', email: '', password: '', phone: '', city: '', institute_id: '' });
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create institute manager', 'error');
    }
  };

  const handleAssignInstituteManager = async (managerId, instituteId) => {
    try {
      await api.put(`/admin/institute-managers/${managerId}/assign`, {
        institute_id: instituteId || null
      });

      showSnackbar(instituteId ? 'Manager assigned successfully' : 'Manager unassigned successfully');
      setAssignInstituteManagerOpen(false);
      setSelectedInstituteManager(null);
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update manager assignment', 'error');
    }
  };

  const handleDeleteInstituteManager = async (managerId) => {
    try {
      await api.delete(`/admin/institute-managers/${managerId}`);
      showSnackbar('Institute manager deleted successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete manager', 'error');
    }
  };

  const openAssignInstituteManagerDialog = (manager) => {
    setSelectedInstituteManager(manager);
    setAssignInstituteManagerOpen(true);
  };

  const openEditLibraryDialog = (library) => {
    setSelectedLibrary(library);
    setEditLibraryData({
      name: library.name,
      address: library.address,
      capacity: library.capacity.toString(),
      city: library.city || null,
      lat: library.lat || '',
      lng: library.lng || '',
      website_enabled: library.website_enabled || false,
      amenities: library.amenities || [],
    });
    setEditLibraryOpen(true);
    setLibraryMenuAnchor(null);
  };

  const openDeleteLibraryDialog = (library) => {
    setSelectedLibrary(library);
    setDeleteLibraryOpen(true);
    setLibraryMenuAnchor(null);
  };

  const openEditPlanDialog = (plan) => {
    setSelectedPlan(plan);
    setEditPlanData({
      price_cents: plan.price_cents.toString(),
      recurring: plan.recurring,
      hours_per_day: plan.hours_per_day,
      first_time_discount: plan.first_time_discount,
    });
    setEditPlanOpen(true);
  };

  const openDeletePlanDialog = (plan) => {
    setSelectedPlan(plan);
    setDeletePlanOpen(true);
  };

  // Render Dashboard Overview
  const renderDashboard = () => (
    <Box>
      <SectionHeader
        eyebrow="Command Center"
        title="Dashboard Overview"
        description="Welcome back. Here's what's happening with your platform today."
        action={
          <GradientButton
            variant="ghost"
            size="small"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
          >
            Refresh
          </GradientButton>
        }
      />

      {/* KPI StatCards */}
      {analytics && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Total Students"
              value={analytics.total_students}
              icon={<People />}
              iconIndex={0}
              index={0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Libraries"
              value={analytics.total_libraries}
              delta={analytics.active_libraries ? `${analytics.active_libraries} active` : null}
              deltaPositive
              icon={<Business />}
              iconIndex={1}
              index={1}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Monthly Revenue"
              value={analytics.mrr}
              prefix="₹"
              delta={analytics.arr ? `ARR ₹${analytics.arr}` : null}
              deltaPositive
              icon={<AttachMoney />}
              iconIndex={2}
              index={2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Seat Utilization"
              value={analytics.seat_utilization_percent}
              suffix="%"
              icon={<TrendingUp />}
              iconIndex={3}
              index={3}
            />
          </Grid>
        </Grid>
      )}

      {/* Quick Stats panels */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <GlassCard sx={{ p: 3, height: '100%' }} gradientRing>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: isDark ? '#F8FAFC' : '#0F172A',
                }}
              >
                Booking Statistics
              </Typography>
              <IconButton
                size="small"
                onClick={fetchDashboardData}
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
                  '&:hover': { color: ADMIN_COLORS.accentIndigo },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Box>
            {analytics && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {[
                  { label: 'Total Bookings', value: analytics.total_bookings, color: null },
                  { label: 'Active Bookings', value: analytics.active_bookings, color: ADMIN_COLORS.success },
                  { label: 'No-shows', value: analytics.no_shows, color: ADMIN_COLORS.danger },
                ].map((row) => (
                  <Box
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={1.5}
                    sx={{
                      borderRadius: 2,
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)'}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: row.color || (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)') }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: row.color || (isDark ? '#F8FAFC' : '#0F172A'),
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </GlassCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <GlassCard sx={{ p: 3, height: '100%' }} gradientRing>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: isDark ? '#F8FAFC' : '#0F172A',
                mb: 2.5,
              }}
            >
              Platform Health
            </Typography>
            {analytics && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {[
                  { label: 'Occupied Hours', value: analytics.occupied_seat_hours, color: null },
                  { label: 'Available Hours', value: analytics.available_seat_hours, color: ADMIN_COLORS.accentIndigo },
                  { label: 'Utilization Rate', value: `${analytics.seat_utilization_percent}%`, color: ADMIN_COLORS.warning },
                ].map((row) => (
                  <Box
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={1.5}
                    sx={{
                      borderRadius: 2,
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)'}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: row.color || (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)') }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: row.color || (isDark ? '#F8FAFC' : '#0F172A'),
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );

  // Render Libraries Management
  const renderLibraries = () => (
    <Box>
      <SectionHeader
        eyebrow="Library Management"
        title="Libraries"
        description="Manage all libraries, their status, and configurations"
        action={
          <GradientButton
            variant="primary"
            startIcon={<Add />}
            onClick={() => setCreateLibraryOpen(true)}
          >
            Add Library
          </GradientButton>
        }
      />

      {/* Search and Filter Bar */}
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or address..."
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setLibraryPage(0);
                  fetchLibraries();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={libraryStatus}
                label="Status"
                onChange={(e) => {
                  setLibraryStatus(e.target.value);
                  setLibraryPage(0);
                  setTimeout(fetchLibraries, 0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setLibraryPage(0);
                fetchLibraries();
              }}
              sx={{ height: 40 }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setLibrarySearch('');
                setLibraryStatus('');
                setLibraryPage(0);
                setTimeout(fetchLibraries, 0);
              }}
              sx={{ height: 40 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'Library' },
          { label: 'Address' },
          { label: 'Capacity' },
          { label: 'Librarian' },
          { label: 'Status' },
          { label: 'Website' },
          { label: 'Actions' },
        ]}
      >
        {libraryLoading ? (
          <ShimmerTableRows count={5} cols={7} />
        ) : libraries.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 'none', p: 0 }}>
              <EmptyState
                icon={<Business />}
                title="No libraries found"
                description="Try adjusting your search or filter criteria."
              />
            </TableCell>
          </TableRow>
        ) : libraries.map((library) => (
          <TableRow key={library.id}>
            <TableCell>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ background: ADMIN_GRADIENTS.accentShort, width: 36, height: 36 }}>
                  <Business fontSize="small" />
                </Avatar>
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography fontWeight="600" variant="body2">{library.name}</Typography>
                    {library.top_rated && (
                      <Tooltip title="Top Rated">
                        <Star sx={{ fontSize: 14, color: '#F59E0B' }} />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </TableCell>
            <TableCell>
              <Box display="flex" alignItems="center" gap={0.5}>
                <LocationOn fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.4)', flexShrink: 0 }} />
                <Typography variant="body2">
                  {library.address.length > 50 ? library.address.substring(0, 50) + '…' : library.address}
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Chip
                label={`${library.capacity} seats`}
                size="small"
                sx={{
                  background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
                  color: isDark ? '#A5B4FC' : '#4F46E5',
                  border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.2)'}`,
                  fontWeight: 600,
                }}
              />
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                {library.librarian?.name || 'Not assigned'}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip
                label={library.active ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  background: library.active
                    ? (isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)')
                    : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'),
                  color: library.active ? ADMIN_COLORS.success : (isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)'),
                  border: `1px solid ${library.active ? 'rgba(16,185,129,0.3)' : 'rgba(15,23,42,0.12)'}`,
                  fontWeight: 600,
                }}
              />
            </TableCell>
            <TableCell>
              {library.website_enabled ? (
                <Tooltip title={`/l/${library.website_slug}`}>
                  <Chip
                    label="On"
                    size="small"
                    onClick={() => window.open(`/l/${library.website_slug}`, '_blank')}
                    sx={{
                      cursor: 'pointer',
                      background: isDark ? 'rgba(34,211,238,0.12)' : 'rgba(34,211,238,0.08)',
                      color: '#22D3EE',
                      border: '1px solid rgba(34,211,238,0.25)',
                      fontWeight: 600,
                    }}
                  />
                </Tooltip>
              ) : (
                <Chip
                  label="Off"
                  size="small"
                  sx={{
                    background: 'transparent',
                    color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(15,23,42,0.3)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'}`,
                  }}
                />
              )}
            </TableCell>
            <TableCell>
              <Box display="flex" gap={1}>
                <GradientButton
                  variant="ghost"
                  size="small"
                  onClick={() => handleToggleLibraryStatus(library.id, library.active)}
                >
                  {library.active ? 'Deactivate' : 'Activate'}
                </GradientButton>
                <Tooltip title="More options">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setSelectedLibrary(library);
                      setLibraryMenuAnchor(e.currentTarget);
                    }}
                    sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </GlassTable>
      <TablePagination
        component="div"
        count={libraryTotal}
        page={libraryPage}
        onPageChange={(e, newPage) => setLibraryPage(newPage)}
        rowsPerPage={libraryRowsPerPage}
        onRowsPerPageChange={(e) => {
          setLibraryRowsPerPage(parseInt(e.target.value, 10));
          setLibraryPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}
      />

      {/* Library Actions Menu */}
      <Menu
        anchorEl={libraryMenuAnchor}
        open={Boolean(libraryMenuAnchor)}
        onClose={() => setLibraryMenuAnchor(null)}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 200 }
        }}
      >
        <MenuItem onClick={() => openEditLibraryDialog(selectedLibrary)}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openManagePlans(selectedLibrary); setLibraryMenuAnchor(null); }}>
          <ListItemIcon><LocalOffer fontSize="small" /></ListItemIcon>
          <ListItemText>Manage Plans</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openManageImages(selectedLibrary); setLibraryMenuAnchor(null); }}>
          <ListItemIcon><PhotoLibrary fontSize="small" /></ListItemIcon>
          <ListItemText>Manage Images</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleToggleTopRated(selectedLibrary?.id, selectedLibrary?.top_rated)}>
          <ListItemIcon><Star fontSize="small" sx={{ color: selectedLibrary?.top_rated ? '#FFC107' : 'inherit' }} /></ListItemIcon>
          <ListItemText>{selectedLibrary?.top_rated ? 'Remove Top Rated' : 'Mark as Top Rated'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => openDeleteLibraryDialog(selectedLibrary)} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete Library</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );

  // Render Institutes Management
  const renderInstitutes = () => (
    <Box>
      <SectionHeader
        eyebrow="Institute Management"
        title="Institutes"
        description="Manage all coaching institutes, their managers, and configurations"
        action={
          <GradientButton
            variant="primary"
            startIcon={<Add />}
            onClick={() => setCreateInstituteOpen(true)}
          >
            Add Institute
          </GradientButton>
        }
      />

      {/* Search and Filter Bar */}
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, address, city..."
              value={instituteSearch}
              onChange={(e) => setInstituteSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setInstitutePage(0);
                  fetchInstitutes();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={instituteStatus}
                label="Status"
                onChange={(e) => {
                  setInstituteStatus(e.target.value);
                  setInstitutePage(0);
                  setTimeout(fetchInstitutes, 0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="City"
              value={instituteCity}
              onChange={(e) => setInstituteCity(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setInstitutePage(0);
                  fetchInstitutes();
                }
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setInstitutePage(0);
                fetchInstitutes();
              }}
              sx={{ height: 40 }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setInstituteSearch('');
                setInstituteStatus('');
                setInstituteCity('');
                setInstitutePage(0);
                setTimeout(fetchInstitutes, 0);
              }}
              sx={{ height: 40 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'Institute' },
          { label: 'City' },
          { label: 'Courses' },
          { label: 'Manager' },
          { label: 'Status' },
          { label: 'Actions' },
        ]}
      >
        {instituteLoading ? (
          <ShimmerTableRows count={5} cols={6} />
        ) : institutes.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ border: 'none', p: 0 }}>
              <EmptyState icon={<School />} title="No institutes found" description="Try adjusting your search or filters." />
            </TableCell>
          </TableRow>
        ) : (
          institutes.map((institute) => (
            <TableRow key={institute.id}>
              <TableCell>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ background: ADMIN_GRADIENTS.accentShort, width: 36, height: 36 }}>
                    <School fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography fontWeight="600" variant="body2">{institute.name}</Typography>
                    {institute.address && (
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)' }}>
                        {institute.address.length > 30 ? institute.address.substring(0, 30) + '…' : institute.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <LocationOn fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.4)' }} />
                  <Typography variant="body2">{institute.city || 'N/A'}</Typography>
                </Box>
              </TableCell>
              <TableCell>
                {institute.courses_offered && institute.courses_offered.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {institute.courses_offered.slice(0, 2).map((course, idx) => (
                      <Chip key={idx} label={course} size="small" sx={{ fontSize: '0.7rem' }} />
                    ))}
                    {institute.courses_offered.length > 2 && (
                      <Chip label={`+${institute.courses_offered.length - 2}`} size="small" />
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>None</Typography>
                )}
              </TableCell>
              <TableCell>
                {institute.manager ? (
                  <Box>
                    <Typography variant="body2" fontWeight="600">{institute.manager.name}</Typography>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)' }}>{institute.manager.email}</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>Not assigned</Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={institute.active ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    background: institute.active
                      ? (isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)')
                      : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'),
                    color: institute.active ? ADMIN_COLORS.success : (isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)'),
                    border: `1px solid ${institute.active ? 'rgba(16,185,129,0.3)' : 'rgba(15,23,42,0.12)'}`,
                    fontWeight: 600,
                  }}
                />
              </TableCell>
              <TableCell>
                <Box display="flex" gap={1}>
                  <GradientButton
                    variant="ghost"
                    size="small"
                    onClick={() => handleToggleInstituteStatus(institute.id, institute.active)}
                  >
                    {institute.active ? 'Deactivate' : 'Activate'}
                  </GradientButton>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEditInstituteDialog(institute)} sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => openDeleteInstituteDialog(institute)} sx={{ color: ADMIN_COLORS.danger }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>
      <TablePagination
        component="div"
        count={instituteTotal}
        page={institutePage}
        onPageChange={(e, newPage) => setInstitutePage(newPage)}
        rowsPerPage={instituteRowsPerPage}
        onRowsPerPageChange={(e) => {
          setInstituteRowsPerPage(parseInt(e.target.value, 10));
          setInstitutePage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}
      />
    </Box>
  );

  // Render Students Management
  const renderStudents = () => (
    <Box>
      <SectionHeader
        eyebrow="User Management"
        title="Student Management"
        description={`View and manage all registered students (${studentTotal} total)`}
        action={
          <GradientButton
            variant="primary"
            startIcon={<PersonAdd />}
            onClick={() => setCreateStudentOpen(true)}
          >
            Add Student
          </GradientButton>
        }
      />

      {/* Search and Filters */}
      <GlassPaper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, or phone..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchStudents()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={studentStatus}
                label="Status"
                onChange={(e) => setStudentStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>City</InputLabel>
              <Select
                value={studentCity}
                label="City"
                onChange={(e) => setStudentCity(e.target.value)}
              >
                <MenuItem value="">All Cities</MenuItem>
                {studentCities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Origin</InputLabel>
              <Select
                value={studentOrigin}
                label="Origin"
                onChange={(e) => setStudentOrigin(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="direct">Direct Signup</MenuItem>
                <MenuItem value="library">Added by Library</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setStudentPage(0);
                fetchStudents();
              }}
              sx={{ borderRadius: 2 }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'Student' },
          { label: 'Email' },
          { label: 'City' },
          { label: 'Phone' },
          { label: 'Origin' },
          { label: 'Status' },
          { label: 'Actions' },
        ]}
      >
        {studentLoading ? (
          <ShimmerTableRows count={8} cols={7} />
        ) : students.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 'none', p: 0 }}>
              <EmptyState icon={<People />} title="No students found" description="Try adjusting your search or filters." />
            </TableCell>
          </TableRow>
        ) : (
          students.map((student) => (
            <TableRow key={student.id}>
              <TableCell>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ background: ADMIN_GRADIENTS.accentShort, width: 36, height: 36, fontSize: '0.875rem', fontWeight: 700 }}>
                    {student.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography fontWeight="600" variant="body2">{student.name}</Typography>
                    {student.age && (
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)' }}>
                        Age: {student.age}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell><Typography variant="body2">{student.email}</Typography></TableCell>
              <TableCell><Typography variant="body2">{student.city || 'N/A'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{student.phone || 'N/A'}</Typography></TableCell>
              <TableCell>
                {student.origin?.type === 'library' ? (
                  <Chip label={student.origin.library_name} size="small" sx={{ fontSize: '0.7rem', background: 'rgba(99,102,241,0.12)', color: isDark ? '#A5B4FC' : '#4F46E5' }} />
                ) : (
                  <Chip label="Direct" size="small" sx={{ fontSize: '0.7rem', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.15)'}` }} />
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={student.blocked ? 'Blocked' : 'Active'}
                  size="small"
                  sx={{
                    background: student.blocked
                      ? 'rgba(244,63,94,0.12)'
                      : (isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)'),
                    color: student.blocked ? ADMIN_COLORS.danger : ADMIN_COLORS.success,
                    border: `1px solid ${student.blocked ? 'rgba(244,63,94,0.3)' : 'rgba(16,185,129,0.3)'}`,
                    fontWeight: 600,
                  }}
                />
              </TableCell>
              <TableCell>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="Edit Student">
                    <IconButton size="small" onClick={() => { setSelectedStudent(student); setEditStudentOpen(true); }} sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset Password">
                    <IconButton size="small" onClick={() => openResetPasswordDialog(student)} sx={{ color: ADMIN_COLORS.warning }}>
                      <LockReset fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <GradientButton
                    variant={student.blocked ? 'primary' : 'danger'}
                    size="small"
                    onClick={() => handleBlockStudent(student.id, !student.blocked)}
                  >
                    {student.blocked ? 'Unblock' : 'Block'}
                  </GradientButton>
                </Box>
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>
      <TablePagination
        component="div"
        count={studentTotal}
        page={studentPage}
        onPageChange={(e, newPage) => setStudentPage(newPage)}
        rowsPerPage={studentRowsPerPage}
        onRowsPerPageChange={(e) => {
          setStudentRowsPerPage(parseInt(e.target.value, 10));
          setStudentPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}
      />
    </Box>
  );

  // Render Bookings
  const renderBookings = () => {
    const getStatusColor = (status) => {
      const colors = {
        pending: 'warning',
        upcoming: 'info',
        active: 'success',
        completed: 'default',
        cancelled: 'error',
        declined: 'error',
        frozen: 'secondary',
        no_show: 'error',
      };
      return colors[status] || 'default';
    };

    const getPaymentStatusColor = (status) => {
      const colors = {
        pending: 'warning',
        pending_payment: 'info',
        paid: 'success',
        failed: 'error',
      };
      return colors[status] || 'default';
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    };

    const formatAmount = (amount) => {
      if (!amount) return '₹0';
      return `₹${(amount / 100).toLocaleString('en-IN')}`;
    };

    return (
      <Box>
        <SectionHeader
          eyebrow="Bookings & Sessions"
          title="Bookings Management"
          description="View and manage all bookings across the platform"
        />

        {/* Stats Cards */}
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Total', value: bookingsStats.total || 0, iconIdx: 0 },
            { label: 'Pending', value: bookingsStats.pending || 0, iconIdx: 3 },
            { label: 'Active', value: bookingsStats.active || 0, iconIdx: 2 },
            { label: 'Completed', value: bookingsStats.completed || 0, iconIdx: 1 },
            { label: 'Cancelled', value: bookingsStats.cancelled || 0, iconIdx: 3 },
            { label: 'Revenue', value: bookingsStats.total_revenue || 0, prefix: '₹', iconIdx: 2 },
          ].map((stat, i) => (
            <Grid item xs={6} sm={4} md={2} key={stat.label}>
              <StatCard label={stat.label} value={stat.value} prefix={stat.prefix} iconIndex={stat.iconIdx} index={i} />
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <GlassPaper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search student"
                value={bookingSearch}
                onChange={(e) => setBookingSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchBookings()}
                size="small"
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                label="Status"
                value={bookingStatus}
                onChange={(e) => setBookingStatus(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="upcoming">Upcoming</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="declined">Declined</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                label="Payment"
                value={bookingPaymentStatus}
                onChange={(e) => setBookingPaymentStatus(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Payments</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="pending_payment">Pay Later</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Library"
                value={bookingLibrary}
                onChange={(e) => setBookingLibrary(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Libraries</MenuItem>
                {bookingsFilters.libraries.map((lib) => (
                  <MenuItem key={lib.id} value={lib.id}>{lib.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setBookingSearch('');
                  setBookingStatus('');
                  setBookingPaymentStatus('');
                  setBookingLibrary('');
                  setBookingStartDate('');
                  setBookingEndDate('');
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </GlassPaper>

        {/* Bookings Table */}
        <GlassTable
          columns={[
            { label: 'Student' },
            { label: 'Library' },
            { label: 'Plan' },
            { label: 'Dates' },
            { label: 'Amount' },
            { label: 'Status' },
            { label: 'Payment' },
            { label: 'Created' },
          ]}
        >
          {bookingLoading ? (
            <ShimmerTableRows count={8} cols={8} />
          ) : bookings.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ border: 'none', p: 0 }}>
                <EmptyState icon={<Event />} title="No bookings found" description="Try adjusting your filters." />
              </TableCell>
            </TableRow>
          ) : (
            bookings.map((booking) => (
              <TableRow key={booking.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="600">{booking.student?.name || 'Unknown'}</Typography>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)' }}>{booking.student?.email}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{booking.library?.name || 'Unknown'}</Typography>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)' }}>{booking.library?.city}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={booking.plan?.plan_type || 'N/A'} size="small" sx={{ background: 'rgba(99,102,241,0.12)', color: isDark ? '#A5B4FC' : '#4F46E5', fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{formatDate(booking.start_date)} – {formatDate(booking.end_date)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="700" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(booking.amount_paid)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={booking.status} size="small" color={getStatusColor(booking.status)} sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={booking.payment_status === 'pending_payment' ? 'Pay Later' : booking.payment_status}
                    color={getPaymentStatusColor(booking.payment_status)}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{formatDate(booking.created_at)}</Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </GlassTable>
        <TablePagination
          component="div"
          count={bookingTotal}
          page={bookingPage}
          onPageChange={(e, newPage) => setBookingPage(newPage)}
          rowsPerPage={bookingRowsPerPage}
          onRowsPerPageChange={(e) => {
            setBookingRowsPerPage(parseInt(e.target.value, 10));
            setBookingPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}
        />
      </Box>
    );
  };

  // Render Analytics — delegates to the full AnalyticsPage component
  const renderAnalytics = () => {
    return <AnalyticsPage />;
    // Legacy analytics stub kept below for reference — unreachable
    if (!analytics) return null; // eslint-disable-line no-unreachable

    // Calculate library-specific metrics
    const libraryMetrics = libraries.map(lib => ({
      ...lib,
      studentCount: students.filter(s => s.library_id === lib.id).length,
      utilization: lib.capacity > 0 ? Math.round((lib.active_sessions || 0) / lib.capacity * 100) : 0
    }));

    // Find top performing library
    const topLibrary = libraryMetrics.reduce((prev, current) =>
      (current.studentCount > prev.studentCount) ? current : prev
    , libraryMetrics[0] || {});

    // Mock data for future features
    const mockGrowthData = [
      { month: 'Jan', students: 120, revenue: 145000 },
      { month: 'Feb', students: 145, revenue: 178000 },
      { month: 'Mar', students: 178, revenue: 215000 },
      { month: 'Apr', students: 195, revenue: 237000 },
      { month: 'May', students: 223, revenue: 268000 },
      { month: 'Jun', students: analytics.total_students, revenue: analytics.mrr * 1000 },
    ];

    const mockRegionalData = [
      { region: 'North', libraries: 3, students: 85, revenue: 102000 },
      { region: 'South', libraries: 2, students: 62, revenue: 74000 },
      { region: 'East', libraries: 2, students: 48, revenue: 58000 },
      { region: 'West', libraries: 1, students: 28, revenue: 34000 },
    ];

    const mockPredictions = {
      nextMonthStudents: Math.round(analytics.total_students * 1.12),
      nextMonthRevenue: Math.round(analytics.mrr * 1.15),
      churnRate: 8.5,
      growthRate: 12.3
    };

    return (
      <Box>
        <SectionHeader
          eyebrow="Analytics"
          title="System Analytics"
          description="Comprehensive insights across all libraries and performance metrics"
        />

        {/* Key Performance Indicators */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Total Revenue (MRR)" value={analytics.mrr} prefix="₹" delta="+12%" deltaPositive icon={<AttachMoney />} iconIndex={2} index={0} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Active Students" value={analytics.total_students} delta={`${analytics.active_libraries} libraries`} deltaPositive icon={<People />} iconIndex={0} index={1} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Avg Utilization" value={analytics.seat_utilization_percent} suffix="%" icon={<TrendingUp />} iconIndex={3} index={2} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Active Libraries" value={analytics.active_libraries} delta={`of ${analytics.total_libraries} total`} deltaPositive icon={<Business />} iconIndex={1} index={3} />
          </Grid>
        </Grid>

        {/* Library Performance Comparison */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} lg={8}>
            <GlassCard sx={{ p: 4, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <BarChart />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Library Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Student enrollment and capacity utilization
                  </Typography>
                </Box>
              </Box>

              <Box>
                {libraryMetrics.map((lib, index) => (
                  <Box key={lib.id} mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                          sx={{
                            background: `linear-gradient(135deg, ${
                              index % 4 === 0 ? '#F26522 0%, #C94E10 100%' :
                              index % 4 === 1 ? '#1B365D 0%, #0F1F3D 100%' :
                              index % 4 === 2 ? '#F7B32B 0%, #C88A00 100%' :
                              '#4A90A4 0%, #2D6B7D 100%'
                            })`,
                            width: 40,
                            height: 40
                          }}
                        >
                          <Business />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="600">
                            {lib.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lib.studentCount} students • {lib.capacity} capacity
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="h6" fontWeight="700">
                          {lib.utilization}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          utilization
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 12,
                        bgcolor: 'action.hover',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${lib.utilization}%`,
                          height: '100%',
                          background: `linear-gradient(90deg, ${
                            index % 4 === 0 ? '#F26522 0%, #C94E10 100%' :
                            index % 4 === 1 ? '#1B365D 0%, #0F1F3D 100%' :
                            index % 4 === 2 ? '#F7B32B 0%, #C88A00 100%' :
                            '#4A90A4 0%, #2D6B7D 100%'
                          })`,
                          transition: 'width 0.5s ease'
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </GlassCard>
          </Grid>

          {/* Top Performer */}
          <Grid item xs={12} lg={4}>
            <GlassCard sx={{ p: 4, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <Star />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Top Performer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Highest enrollment
                  </Typography>
                </Box>
              </Box>

              {topLibrary && (
                <Box textAlign="center" py={3}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)'
                    }}
                  >
                    <Business sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" fontWeight="700" gutterBottom>
                    {topLibrary.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    {topLibrary.address?.substring(0, 40)}...
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="700" color="primary">
                          {topLibrary.studentCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Students
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="700" color="success.main">
                          {topLibrary.utilization}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Utilization
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </GlassCard>
          </Grid>
        </Grid>

        {/* Booking & Revenue Metrics */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <GlassCard sx={{ p: 4 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #1B365D 0%, #0F1F3D 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <PieChart />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Booking Distribution
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status breakdown
                  </Typography>
                </Box>
              </Box>

              <Box>
                {[
                  { label: 'Active Bookings', value: analytics.active_bookings, color: '#4caf50', total: analytics.total_bookings },
                  { label: 'Completed', value: analytics.total_bookings - analytics.active_bookings - analytics.no_shows, color: '#2196f3', total: analytics.total_bookings },
                  { label: 'No Shows', value: analytics.no_shows, color: '#f44336', total: analytics.total_bookings },
                ].map((item, index) => (
                  <Box key={index} mb={2.5}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" fontWeight="600">
                        {item.label}
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {item.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${item.total > 0 ? (item.value / item.total) * 100 : 0}%`,
                          height: '100%',
                          bgcolor: item.color,
                          transition: 'width 0.5s ease'
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <GlassCard sx={{ p: 4 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <Insights />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Revenue Insights
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Financial performance
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      MRR
                    </Typography>
                    <Typography variant="h5" fontWeight="700">
                      ₹{analytics.mrr}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      ARR
                    </Typography>
                    <Typography variant="h5" fontWeight="700">
                      ₹{analytics.arr}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Revenue per Student
                    </Typography>
                    <Typography variant="h5" fontWeight="700">
                      ₹{analytics.total_students > 0 ? Math.round(analytics.mrr / analytics.total_students) : 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Mock Future Features */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <GlassCard sx={{ p: 4 }}>
              {/* Coming Soon Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(2px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}
              >
                <Chip
                  label="Coming Soon"
                  color="primary"
                  sx={{ fontWeight: 700, px: 2, py: 2.5 }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #F7B32B 0%, #C88A00 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <ShowChart />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Growth Trends
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    6-month student & revenue growth
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ opacity: 0.4 }}>
                {mockGrowthData.map((item, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2">{item.month}</Typography>
                    <Box display="flex" gap={2}>
                      <Typography variant="body2" fontWeight="600">{item.students} students</Typography>
                      <Typography variant="body2" color="success.main">₹{item.revenue.toLocaleString()}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <GlassCard sx={{ p: 4 }}>
              {/* Coming Soon Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(2px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}
              >
                <Chip
                  label="Coming Soon"
                  color="primary"
                  sx={{ fontWeight: 700, px: 2, py: 2.5 }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #1B365D 0%, #0F1F3D 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <Language />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Regional Distribution
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Libraries by region
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ opacity: 0.4 }}>
                {mockRegionalData.map((region, index) => (
                  <Box key={index} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight="600">{region.region}</Typography>
                      <Typography variant="body2">{region.libraries} libraries • {region.students} students</Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(region.students / mockRegionalData.reduce((sum, r) => sum + r.students, 0)) * 100}%`,
                          height: '100%',
                          bgcolor: index % 2 === 0 ? theme.palette.secondary.main : theme.palette.secondary.dark
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12}>
            <GlassCard sx={{ p: 4 }}>
              {/* Coming Soon Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(2px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}
              >
                <Chip
                  label="Coming Soon"
                  color="primary"
                  sx={{ fontWeight: 700, px: 2, py: 2.5 }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <AutoGraph />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Predictive Analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    AI-powered forecasting and insights
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ opacity: 0.4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="700" color="primary">
                        {mockPredictions.nextMonthStudents}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Predicted Students (Next Month)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="700" color="success.main">
                        ₹{mockPredictions.nextMonthRevenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Predicted Revenue (Next Month)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="700" color="warning.main">
                        {mockPredictions.churnRate}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Churn Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="700" color="info.main">
                        +{mockPredictions.growthRate}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Growth Rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </GlassCard>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render Active Sessions
  const renderSessions = () => {
    const calculateDuration = (enteredAt) => {
      const now = new Date();
      const entered = new Date(enteredAt);
      const diffMs = now - entered;
      const diffMins = Math.floor(diffMs / 1000 / 60);
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };

    const formatTime = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    // Group sessions by library
    const sessionsByLibrary = {};
    activeSessions.forEach(session => {
      const libraryId = session.library?.id || 'unknown';
      if (!sessionsByLibrary[libraryId]) {
        sessionsByLibrary[libraryId] = {
          library: session.library,
          sessions: []
        };
      }
      sessionsByLibrary[libraryId].sessions.push(session);
    });

    return (
      <Box>
        <SectionHeader
          eyebrow="Bookings & Sessions"
          title="Active Sessions"
          description="Real-time view of all active library sessions across the platform"
        />

        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Total Active" value={activeSessions.length} delta="sessions right now" deltaPositive icon={<Groups />} iconIndex={0} index={0} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Libraries Active" value={Object.keys(sessionsByLibrary).length} delta={`of ${libraries.length} total`} deltaPositive icon={<Business />} iconIndex={1} index={1} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Avg Duration"
              value={activeSessions.length > 0 ? Math.round(activeSessions.reduce((sum, s) => { const diff = new Date() - new Date(s.entered_at); return sum + (diff / 1000 / 60); }, 0) / activeSessions.length) : 0}
              suffix="m"
              delta="per session"
              deltaPositive
              icon={<AccessTime />}
              iconIndex={2}
              index={2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Utilization" value={analytics?.seat_utilization_percent || 0} suffix="%" delta="system-wide" deltaPositive icon={<TrendingUp />} iconIndex={3} index={3} />
          </Grid>
        </Grid>

        {/* Sessions Table */}
        {activeSessions.length === 0 ? (
          <GlassCard sx={{ p: 6 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                bgcolor: 'action.hover'
              }}
            >
              <Groups sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              No Active Sessions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are currently no students checked in to any library
            </Typography>
          </GlassCard>
        ) : (
          <GlassCard>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Student</strong></TableCell>
                    <TableCell><strong>Library</strong></TableCell>
                    <TableCell><strong>Check-in Time</strong></TableCell>
                    <TableCell><strong>Duration</strong></TableCell>
                    <TableCell><strong>Booking</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeSessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            sx={{
                              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
                            }}
                          >
                            {session.user?.name?.charAt(0).toUpperCase() || 'S'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="600">
                              {session.user?.name || 'Unknown Student'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.user?.email || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight="600">
                              {session.library?.name || 'Unknown Library'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.library?.address?.substring(0, 40) || 'N/A'}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {formatTime(session.entered_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(session.entered_at)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={calculateDuration(session.entered_at)}
                          size="small"
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 600,
                            fontFamily: 'monospace'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {session.booking ? (
                          <Chip
                            label={`Booking #${session.booking.id}`}
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                        ) : (
                          <Chip
                            label="Walk-in"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="Active"
                          size="small"
                          icon={<CheckCircle />}
                          color="success"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </GlassCard>
        )}

        {/* Refresh Button */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
            sx={{ borderRadius: 3 }}
          >
            Refresh Sessions
          </Button>
        </Box>
      </Box>
    );
  };

  // Render Librarians Management
  const renderLibrarians = () => (
    <Box>
      <SectionHeader
        eyebrow="Library Management"
        title="Librarian Management"
        description={`Manage librarian accounts and library assignments (${librarianTotal} total)`}
        action={
          <GradientButton variant="primary" startIcon={<PersonAdd />} onClick={() => setCreateLibrarianOpen(true)}>
            Add Librarian
          </GradientButton>
        }
      />

      {/* Search and Filters */}
      <GlassPaper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email..."
              value={librarianSearch}
              onChange={(e) => setLibrarianSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchLibrarians()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={librarianStatus}
                label="Status"
                onChange={(e) => setLibrarianStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setLibrarianPage(0);
                fetchLibrarians();
              }}
              sx={{ borderRadius: 2 }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setLibrarianSearch('');
                setLibrarianStatus('');
                setLibrarianPage(0);
                setTimeout(() => fetchLibrarians(), 0);
              }}
              sx={{ borderRadius: 2 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'Librarian' },
          { label: 'Email' },
          { label: 'Phone' },
          { label: 'Assigned Library' },
          { label: 'Status' },
          { label: 'Actions' },
        ]}
      >
        {librarianLoading ? (
          <ShimmerTableRows count={6} cols={6} />
        ) : librarians.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ border: 'none', p: 0 }}>
              <EmptyState
                icon={<Person />}
                title="No Librarians Found"
                description={librarianSearch || librarianStatus ? 'Try adjusting your search filters.' : 'Create your first librarian account.'}
                action={!librarianSearch && !librarianStatus ? (
                  <GradientButton variant="primary" startIcon={<PersonAdd />} onClick={() => setCreateLibrarianOpen(true)}>Add Librarian</GradientButton>
                ) : null}
              />
            </TableCell>
          </TableRow>
        ) : librarians.map((librarian) => (
          <TableRow key={librarian.id}>
            <TableCell>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ background: ADMIN_GRADIENTS.accentShort, width: 36, height: 36, fontSize: '0.875rem', fontWeight: 700 }}>
                  {librarian.name?.charAt(0).toUpperCase() || 'L'}
                </Avatar>
                <Typography variant="body2" fontWeight="600">{librarian.name}</Typography>
              </Box>
            </TableCell>
            <TableCell><Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(15,23,42,0.65)' }}>{librarian.email}</Typography></TableCell>
            <TableCell><Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(15,23,42,0.65)' }}>{librarian.phone || 'N/A'}</Typography></TableCell>
            <TableCell>
              {librarian.library ? (
                <Box>
                  <Typography variant="body2" fontWeight="600">{librarian.library.name}</Typography>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)' }}>
                    {librarian.library.address?.substring(0, 30)}…
                  </Typography>
                </Box>
              ) : (
                <Chip label="Not Assigned" size="small" sx={{ background: 'rgba(245,158,11,0.12)', color: ADMIN_COLORS.warning, border: '1px solid rgba(245,158,11,0.3)' }} />
              )}
            </TableCell>
            <TableCell>
              <Chip
                label={librarian.blocked ? 'Blocked' : 'Active'}
                size="small"
                sx={{
                  background: librarian.blocked ? 'rgba(244,63,94,0.12)' : (isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)'),
                  color: librarian.blocked ? ADMIN_COLORS.danger : ADMIN_COLORS.success,
                  border: `1px solid ${librarian.blocked ? 'rgba(244,63,94,0.3)' : 'rgba(16,185,129,0.3)'}`,
                  fontWeight: 600,
                }}
              />
            </TableCell>
            <TableCell>
              <Box display="flex" gap={0.5}>
                <Tooltip title="Edit Librarian">
                  <IconButton size="small" onClick={() => { setSelectedLibrarian(librarian); setEditLibrarianData({ name: librarian.name || '', email: librarian.email || '', phone: librarian.phone || '', city: librarian.city || '' }); setEditLibrarianOpen(true); }} sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Assign Library">
                  <IconButton size="small" onClick={() => openAssignLibrarianDialog(librarian)} sx={{ color: ADMIN_COLORS.accentIndigo }}>
                    <Business fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Password">
                  <IconButton size="small" onClick={() => openResetPasswordDialog(librarian)} sx={{ color: ADMIN_COLORS.warning }}>
                    <LockReset fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => { if (window.confirm(`Delete librarian ${librarian.name}?`)) { handleDeleteLibrarian(librarian.id); } }} sx={{ color: ADMIN_COLORS.danger }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </GlassTable>
      <TablePagination
        component="div"
        count={librarianTotal}
        page={librarianPage}
        onPageChange={(e, newPage) => setLibrarianPage(newPage)}
        rowsPerPage={librarianRowsPerPage}
        onRowsPerPageChange={(e) => {
          setLibrarianRowsPerPage(parseInt(e.target.value, 10));
          setLibrarianPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}
      />
    </Box>
  );

  // Render Institute Managers Management
  const renderInstituteManagers = () => (
    <Box>
      <SectionHeader
        eyebrow="Institute Management"
        title="Institute Manager Management"
        description={`Manage institute managers and their assignments (${managerTotal} total)`}
        action={
          <GradientButton variant="primary" startIcon={<Add />} onClick={() => setCreateInstituteManagerOpen(true)}>
            Add Manager
          </GradientButton>
        }
      />

      {/* Search and Filters */}
      <GlassPaper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, or phone..."
              value={managerSearch}
              onChange={(e) => setManagerSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchInstituteManagers()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={managerStatus}
                label="Status"
                onChange={(e) => setManagerStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setManagerPage(0);
                fetchInstituteManagers();
              }}
              sx={{ borderRadius: 2 }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setManagerSearch('');
                setManagerStatus('');
                setManagerPage(0);
                setTimeout(() => fetchInstituteManagers(), 0);
              }}
              sx={{ borderRadius: 2 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'Manager' },
          { label: 'Email' },
          { label: 'Phone' },
          { label: 'Assigned Institute' },
          { label: 'Status' },
          { label: 'Actions' },
        ]}
      >
        {managerLoading ? (
          <ShimmerTableRows count={6} cols={6} />
        ) : instituteManagers.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ border: 'none', p: 0 }}>
              <EmptyState
                icon={<AdminPanelSettings />}
                title="No Institute Managers Found"
                description={managerSearch || managerStatus ? 'Try adjusting your search filters.' : 'Add managers to manage institutes.'}
                action={!managerSearch && !managerStatus ? (
                  <GradientButton variant="primary" startIcon={<Add />} onClick={() => setCreateInstituteManagerOpen(true)}>Add Manager</GradientButton>
                ) : null}
              />
            </TableCell>
          </TableRow>
        ) : instituteManagers.map((manager) => (
          <TableRow key={manager.id}>
            <TableCell>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ background: ADMIN_GRADIENTS.accentShort, width: 36, height: 36 }}>
                  <AdminPanelSettings fontSize="small" />
                </Avatar>
                <Typography fontWeight="600" variant="body2">{manager.name}</Typography>
              </Box>
            </TableCell>
            <TableCell><Typography variant="body2">{manager.email}</Typography></TableCell>
            <TableCell><Typography variant="body2">{manager.phone || 'N/A'}</Typography></TableCell>
            <TableCell>
              {manager.institute ? (
                <Box>
                  <Typography variant="body2" fontWeight="600">{manager.institute.name}</Typography>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)' }}>{manager.institute.city}</Typography>
                </Box>
              ) : (
                <Chip label="Unassigned" size="small" sx={{ background: 'rgba(245,158,11,0.12)', color: ADMIN_COLORS.warning, border: '1px solid rgba(245,158,11,0.3)' }} />
              )}
            </TableCell>
            <TableCell>
              <Chip
                label={manager.blocked ? 'Blocked' : 'Active'}
                size="small"
                sx={{
                  background: manager.blocked ? 'rgba(244,63,94,0.12)' : (isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)'),
                  color: manager.blocked ? ADMIN_COLORS.danger : ADMIN_COLORS.success,
                  border: `1px solid ${manager.blocked ? 'rgba(244,63,94,0.3)' : 'rgba(16,185,129,0.3)'}`,
                  fontWeight: 600,
                }}
              />
            </TableCell>
            <TableCell>
              <Box display="flex" gap={0.5}>
                <GradientButton variant="ghost" size="small" onClick={() => openAssignInstituteManagerDialog(manager)}>
                  {manager.institute ? 'Reassign' : 'Assign'}
                </GradientButton>
                <Tooltip title="Reset Password">
                  <IconButton size="small" onClick={() => openResetPasswordDialog(manager)} sx={{ color: ADMIN_COLORS.warning }}>
                    <LockReset fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Block/Unblock">
                  <IconButton size="small" onClick={() => handleBlockStudent(manager.id, !manager.blocked)} sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}>
                    <Block fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDeleteInstituteManager(manager.id)} sx={{ color: ADMIN_COLORS.danger }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </GlassTable>
      <TablePagination
        component="div"
        count={managerTotal}
        page={managerPage}
        onPageChange={(e, newPage) => setManagerPage(newPage)}
        rowsPerPage={managerRowsPerPage}
        onRowsPerPageChange={(e) => {
          setManagerRowsPerPage(parseInt(e.target.value, 10));
          setManagerPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}
      />
    </Box>
  );


  // Render Plans Overview
  const renderPlans = () => (
    <Box>
      <SectionHeader
        eyebrow="Library Management"
        title="Subscription Plans"
        description="Manage plans for each library"
      />

      <Grid container spacing={3}>
        {libraries.map((library) => (
          <Grid item xs={12} md={6} lg={4} key={library.id}>
            <GlassCard interactive sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{
                    background: ADMIN_GRADIENTS.accentShort,
                    width: 48,
                    height: 48
                  }}>
                    <Business />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="700">
                      {library.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {library.address.substring(0, 30)}...
                    </Typography>
                  </Box>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => openManagePlans(library)}
                  sx={{ borderRadius: 3, mt: 2 }}
                >
                  Manage Plans
                </Button>
            </GlassCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return renderDashboard();
      case 'libraries':
        return renderLibraries();
      case 'institutes':
        return renderInstitutes();
      case 'students':
        return renderStudents();
      case 'librarians':
        return renderLibrarians();
      case 'institute-managers':
        return renderInstituteManagers();
      case 'institute-leads':
        return <InstituteLeadsManagement />;
      case 'marketing-users':
        return <MarketingUsersManagement />;
      case 'sales-users':
        return <SalesUsersManagement />;
      case 'seo-users':
        return <SeoUsersManagement />;
      case 'withdrawals':
        return <WithdrawalManagement />;
      case 'finance':
        return <FinancePanel />;
      case 'plans':
        return renderPlans();
      case 'analytics':
        return renderAnalytics();
      case 'sessions':
        return renderSessions();
      case 'bookings':
        return renderBookings();
      default:
        return renderDashboard();
    }
  };

  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        position: 'relative',
        '@media (max-width: 1600px)': { zoom: 0.92 },
        '@media (max-width: 1440px)': { zoom: 0.90 },
        '@media (max-width: 1366px)': { zoom: 0.88 },
        '@media (max-width: 1280px)': { zoom: 0.85 },
      }}
    >
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...g.surface,
          borderRadius: 0,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" fontWeight="700" sx={{ flexGrow: 1 }}>
            Admin Portal
          </Typography>

          {/* Theme Toggle */}
          <ThemeToggle sx={{ mr: 1 }} />

          {/* Search Bar */}
          <Box sx={{
            display: { xs: 'none', sm: 'block' },
            mr: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.06)',
            border: `1px solid ${isDark ? 'transparent' : 'rgba(15,23,42,0.08)'}`,
            borderRadius: 3,
            px: 2
          }}>
            <Box display="flex" alignItems="center">
              <Search sx={{ mr: 1, color: isDark ? 'white' : '#1F2937' }} />
              <input
                placeholder="Search..."
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: isDark ? 'white' : '#1F2937',
                  outline: 'none',
                  padding: '8px 0',
                  width: '200px'
                }}
              />
            </Box>
          </Box>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              color="inherit"
              sx={{ mr: 1 }}
              onClick={(e) => {
                setNotificationMenuAnchor(e.currentTarget);
                fetchNotifications();
              }}
            >
              <Badge badgeContent={notificationUnreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={notificationMenuAnchor}
            open={Boolean(notificationMenuAnchor)}
            onClose={() => setNotificationMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: 2,
                minWidth: 360,
                maxWidth: 400,
                maxHeight: 480,
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="700">Notifications</Typography>
              {notificationUnreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllRead}>Mark all read</Button>
              )}
            </Box>
            {notificationLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No notifications</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                {notifications.map((notification) => (
                  <MenuItem
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {!notification.is_read && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        )}
                        <Typography variant="subtitle2" fontWeight={notification.is_read ? 500 : 700} sx={{ flex: 1 }}>
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.type}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: notification.type === 'booking' ? 'info.light' :
                                    notification.type === 'payment' ? 'success.light' :
                                    notification.type === 'withdrawal' ? 'warning.light' : 'grey.300',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.4 }}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(notification.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Box>
            )}
            {notifications.length > 0 && (
              <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Button size="small" onClick={() => { setNotificationMenuAnchor(null); setCurrentView('dashboard'); }}>
                  View All Notifications
                </Button>
              </Box>
            )}
          </Menu>

          {/* User Menu */}
          <Tooltip title="Account">
            <IconButton
              color="inherit"
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            >
              <AccountCircle />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            PaperProps={{
              sx: { borderRadius: 2, minWidth: 200 }
            }}
          >
            <MenuItem onClick={() => { showSnackbar('Coming Soon!', 'info'); setUserMenuAnchor(null); }}>
              <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { showSnackbar('Coming Soon!', 'info'); setUserMenuAnchor(null); }}>
              <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              setUserMenuAnchor(null);
              navigate('/login');
              setTimeout(() => logout(), 0);
            }}>
              <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: isDark
              ? 'rgba(11,15,30,0.92)'
              : 'rgba(255,255,255,0.88)',
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.08)'}`,
            color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(15,23,42,0.9)',
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <Toolbar sx={{
          minHeight: '64px !important',
          background: ADMIN_GRADIENTS.accentShort,
          boxShadow: '0 2px 16px rgba(99,102,241,0.35)',
        }}>
          <AdminPanelSettings sx={{ mr: 2 }} />
          <Typography variant="h6" fontWeight="700">
            BookMyLibrary
          </Typography>
        </Toolbar>
        <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)' }} />
        <List sx={{ px: 2, py: 2 }}>
          {menuSections.map((section) => (
            <React.Fragment key={section.id}>
              {/* Section with collapsible header */}
              {section.label ? (
                <>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => toggleSection(section.id)}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.6)', minWidth: 36 }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.6)'
                        }}
                      />
                      {expandedSections[section.id] ? (
                        <ExpandLess sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }} />
                      ) : (
                        <ExpandMore sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={expandedSections[section.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 1 }}>
                      {section.items.map((item) => (
                        <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleMenuClick(item.id, item.implemented)}
                            sx={{
                              borderRadius: 2,
                              py: 0.75,
                              bgcolor: currentView === item.id ? 'rgba(99,102,241,0.18)' : 'transparent',
                              '&:hover': {
                                bgcolor: currentView === item.id ? 'rgba(99,102,241,0.25)' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)'),

                              },
                              border: currentView === item.id ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ListItemIcon sx={{ color: currentView === item.id ? (isDark ? 'white' : '#6366F1') : (isDark ? 'white' : '#475569'), minWidth: 36 }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.text}
                              primaryTypographyProps={{
                                fontWeight: currentView === item.id ? 700 : 500,
                                fontSize: '0.9rem'
                              }}
                            />
                            {!item.implemented && (
                              <Chip
                                label="Soon"
                                size="small"
                                sx={{
                                  bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.06)',
                                  color: isDark ? 'white' : '#475569',
                                  fontSize: '0.6rem',
                                  height: 18
                                }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                /* Section without header - render items directly */
                section.items.map((item) => (
                  <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleMenuClick(item.id, item.implemented)}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        bgcolor: currentView === item.id ? 'rgba(99,102,241,0.18)' : 'transparent',
                        '&:hover': {
                          bgcolor: currentView === item.id ? 'rgba(99,102,241,0.25)' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)'),
                        },
                        border: currentView === item.id ? '1px solid rgba(99,102,241,0.45)' : '1px solid transparent',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ListItemIcon sx={{ color: currentView === item.id ? (isDark ? 'white' : '#6366F1') : (isDark ? 'white' : '#475569'), minWidth: 36 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: currentView === item.id ? 700 : 500,
                          fontSize: '0.9rem'
                        }}
                      />
                      {!item.implemented && (
                        <Chip
                          label="Soon"
                          size="small"
                          sx={{
                            bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.06)',
                            color: isDark ? 'white' : '#475569',
                            fontSize: '0.6rem',
                            height: 18
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))
              )}
              {/* Add divider after sections with labels, but not after the last section */}
              {section.label && section.id !== 'other-section' && (
                <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)', my: 1 }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          position: 'relative',
          zIndex: 1,
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          ml: { md: sidebarOpen ? 0 : 0 },
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <Box
                sx={{
                  ...g.surface,
                  borderRadius: 3,
                  p: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CircularProgress
                  sx={{
                    color: ADMIN_COLORS.accentIndigo,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}
                >
                  Loading dashboard…
                </Typography>
              </Box>
            </Box>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentView}
                initial={reducedMotion ? {} : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reducedMotion ? {} : { opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          )}
        </Container>
      </Box>

      {/* Dialogs remain the same */}
      {/* Create Library Dialog */}
      <Dialog
        open={createLibraryOpen}
        onClose={() => setCreateLibraryOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionProps={{
          onEnter: () => fetchUnassignedLibrarians()
        }}
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">Create New Library</Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Library Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Library Name"
                  value={newLibrary.name}
                  onChange={(e) => setNewLibrary({ ...newLibrary, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Capacity"
                  type="number"
                  value={newLibrary.capacity}
                  onChange={(e) => setNewLibrary({ ...newLibrary, capacity: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={newLibrary.address}
                  onChange={(e) => setNewLibrary({ ...newLibrary, address: e.target.value })}
                  required
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={INDIAN_CITIES}
                  value={newLibrary.city}
                  onChange={(event, newValue) => setNewLibrary({ ...newLibrary, city: newValue })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newLibrary.website_enabled}
                      onChange={(e) => setNewLibrary({ ...newLibrary, website_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Website"
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>

            {newLibrary.website_enabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A landing page will be created for this library. Librarian can customize theme and colors.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Librarian Assignment Section */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Librarian Assignment (Optional)
            </Typography>
            <FormControl component="fieldset" sx={{ mt: 1 }}>
              <RadioGroup
                value={newLibrary.librarianMode}
                onChange={(e) => setNewLibrary({
                  ...newLibrary,
                  librarianMode: e.target.value,
                  existing_librarian_id: null,
                  librarian_name: '',
                  librarian_email: '',
                  librarian_password: '',
                  librarian_phone: ''
                })}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio />}
                  label="No librarian (assign later)"
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label="Assign existing librarian"
                />
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Create new librarian account"
                />
              </RadioGroup>
            </FormControl>

            {/* Existing Librarian Selection */}
            {newLibrary.librarianMode === 'existing' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Autocomplete
                  options={unassignedLibrarians}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  value={unassignedLibrarians.find(l => l.id === newLibrary.existing_librarian_id) || null}
                  onChange={(event, newValue) => setNewLibrary({
                    ...newLibrary,
                    existing_librarian_id: newValue?.id || null
                  })}
                  loading={unassignedLibrariansLoading}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography fontWeight="600">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Unassigned Librarian"
                      placeholder="Search by name or email..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {unassignedLibrariansLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  fullWidth
                />
                {unassignedLibrarians.length === 0 && !unassignedLibrariansLoading && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No unassigned librarians available. Create a new librarian account instead.
                  </Alert>
                )}
              </Box>
            )}

            {/* New Librarian Form */}
            {newLibrary.librarianMode === 'new' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Librarian Name"
                      value={newLibrary.librarian_name}
                      onChange={(e) => setNewLibrary({ ...newLibrary, librarian_name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Librarian Email"
                      type="email"
                      value={newLibrary.librarian_email}
                      onChange={(e) => setNewLibrary({ ...newLibrary, librarian_email: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Librarian Password"
                      type="password"
                      value={newLibrary.librarian_password}
                      onChange={(e) => setNewLibrary({ ...newLibrary, librarian_password: e.target.value })}
                      required
                      helperText="Min 6 characters"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Librarian Phone"
                      value={newLibrary.librarian_phone}
                      onChange={(e) => setNewLibrary({ ...newLibrary, librarian_phone: e.target.value })}
                      placeholder="XXXXXXXXXX"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="action" />
                            <Typography sx={{ ml: 0.5 }}>+91</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateLibraryOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateLibrary}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
            }}
          >
            Create Library
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Library Dialog */}
      <Dialog open={editLibraryOpen} onClose={() => setEditLibraryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">Edit Library</Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              fullWidth
              label="Library Name"
              value={editLibraryData.name}
              onChange={(e) => setEditLibraryData({ ...editLibraryData, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Address"
              value={editLibraryData.address}
              onChange={(e) => setEditLibraryData({ ...editLibraryData, address: e.target.value })}
              margin="normal"
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Capacity"
              type="number"
              value={editLibraryData.capacity}
              onChange={(e) => setEditLibraryData({ ...editLibraryData, capacity: e.target.value })}
              margin="normal"
            />
            <Autocomplete
              options={INDIAN_CITIES}
              value={editLibraryData.city}
              onChange={(event, newValue) => setEditLibraryData({ ...editLibraryData, city: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  margin="normal"
                />
              )}
              fullWidth
            />
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Latitude"
                value={editLibraryData.lat}
                onChange={(e) => setEditLibraryData({ ...editLibraryData, lat: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Longitude"
                value={editLibraryData.lng}
                onChange={(e) => setEditLibraryData({ ...editLibraryData, lng: e.target.value })}
                margin="normal"
              />
            </Box>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
              Amenities
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Select Amenities</InputLabel>
              <Select
                multiple
                value={editLibraryData.amenities}
                onChange={(e) => setEditLibraryData({ ...editLibraryData, amenities: e.target.value })}
                label="Select Amenities"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 300 },
                  },
                }}
              >
                {AMENITIES_OPTIONS.map((amenity) => (
                  <MenuItem key={amenity} value={amenity}>
                    <Checkbox checked={editLibraryData.amenities.indexOf(amenity) > -1} size="small" />
                    <ListItemText primary={amenity} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editLibraryData.amenities.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {editLibraryData.amenities.length} amenities selected
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={editLibraryData.website_enabled}
                  onChange={(e) => setEditLibraryData({ ...editLibraryData, website_enabled: e.target.checked })}
                />
              }
              label="Enable Website"
              sx={{ mt: 2 }}
            />
            {editLibraryData.website_enabled && selectedLibrary?.website_slug && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Website URL: <strong>/l/{selectedLibrary.website_slug}</strong>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditLibraryOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditLibrary}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
            }}
          >
            Update Library
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Library Dialog */}
      <Dialog open={deleteLibraryOpen} onClose={() => setDeleteLibraryOpen(false)} maxWidth="sm">
        <DialogTitle>
          <Typography variant="h5" fontWeight="700" color="error">
            Delete Library?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedLibrary?.name}</strong>? This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Libraries with active bookings cannot be deleted.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteLibraryOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteLibrary}
            variant="contained"
            color="error"
            size="large"
            sx={{ borderRadius: 3 }}
          >
            Delete Library
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Images Dialog */}
      <Dialog
        open={manageImagesOpen}
        onClose={() => setManageImagesOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="700">
              Manage Images - {selectedLibrary?.name}
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={uploadingImages ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
              disabled={uploadingImages}
              sx={{
                borderRadius: 3,
                background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
              }}
            >
              {uploadingImages ? 'Uploading...' : 'Upload Images'}
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {imagesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : libraryImages.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              No images uploaded for this library. Click "Upload Images" to add photos.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {libraryImages.map((imageUrl, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Paper
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      '&:hover .delete-overlay': {
                        opacity: 1,
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={`${(process.env.REACT_APP_API_URL || '').replace(/\/api$/, '')}${imageUrl}`}
                      alt={`Library image ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: '100%',
                        height: 150,
                        objectFit: 'cover',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Box
                      className="delete-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      <IconButton
                        onClick={() => handleDeleteImage(imageUrl)}
                        sx={{
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setManageImagesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Plans Dialog */}
      <Dialog open={managePlansOpen} onClose={() => setManagePlansOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="700">
              Manage Plans - {selectedLibrary?.name}
            </Typography>
            {(() => {
              const existingPlanTypes = libraryPlans.map(p => p.plan_type);
              const availablePlanTypes = Object.keys(PLAN_TYPES).filter(
                type => !existingPlanTypes.includes(type)
              );
              return availablePlanTypes.length > 0 ? (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreatePlanOpen(true)}
                  size="small"
                  sx={{
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
                  }}
                >
                  Add Plan
                </Button>
              ) : null;
            })()}
          </Box>
        </DialogTitle>
        <DialogContent>
          {libraryPlans.length === 0 ? (
            <Alert severity="info">No plans available for this library.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Plan Type</strong></TableCell>
                    <TableCell><strong>Price</strong></TableCell>
                    <TableCell><strong>Duration</strong></TableCell>
                    <TableCell><strong>Hours/Day</strong></TableCell>
                    <TableCell><strong>New User Discount</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {libraryPlans.map((plan) => (
                    <TableRow key={plan.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {plan.name}
                          {plan.hours_per_day && (
                            <Typography component="span" variant="body2" color="info.main" sx={{ ml: 1 }}>
                              ({plan.hours_per_day}h/day)
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          ₹{plan.price_cents}
                        </Typography>
                      </TableCell>
                      <TableCell>{plan.duration_days} days</TableCell>
                      <TableCell>
                        {plan.hours_per_day ? (
                          <Chip label={`${plan.hours_per_day}h`} size="small" color="info" variant="outlined" />
                        ) : (
                          <Chip label="Unlimited" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {plan.first_time_discount ? (
                          <Chip label={`${plan.first_time_discount}% off`} size="small" color="warning" sx={{ fontWeight: 600 }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plan.recurring ? 'Recurring' : 'One-time'}
                          size="small"
                          color={plan.recurring ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditPlanDialog(plan)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => openDeletePlanDialog(plan)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setManagePlansOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Plan Dialog */}
      <Dialog open={createPlanOpen} onClose={() => setCreatePlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">Create New Plan</Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Plan Type</InputLabel>
              <Select
                value={newPlan.plan_type}
                onChange={(e) => setNewPlan({ ...newPlan, plan_type: e.target.value })}
                label="Plan Type"
              >
                {(() => {
                  const existingPlanTypes = libraryPlans.map(p => p.plan_type);
                  return Object.keys(PLAN_TYPES)
                    .filter(type => !existingPlanTypes.includes(type))
                    .map((type) => (
                      <MenuItem key={type} value={type}>
                        {PLAN_TYPES[type].label} ({PLAN_TYPES[type].duration})
                      </MenuItem>
                    ));
                })()}
              </Select>
            </FormControl>

            {newPlan.plan_type && (
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                {PLAN_TYPES[newPlan.plan_type]?.description}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Price (₹)"
              type="number"
              value={newPlan.price_cents}
              onChange={(e) => setNewPlan({ ...newPlan, price_cents: e.target.value })}
              margin="normal"
              required
              helperText="Enter amount in rupees (e.g., 100 for ₹100)"
              inputProps={{ min: '0' }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Daily Hours Limit</InputLabel>
              <Select
                value={newPlan.hours_per_day === null ? 'unlimited' : newPlan.hours_per_day}
                onChange={(e) => {
                  const value = e.target.value === 'unlimited' ? null : e.target.value;
                  setNewPlan({ ...newPlan, hours_per_day: value });
                }}
                label="Daily Hours Limit"
              >
                {HOURS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'unlimited'} value={option.value === null ? 'unlimited' : option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={newPlan.first_time_discount !== null}
                  onChange={(e) => {
                    setNewPlan({
                      ...newPlan,
                      first_time_discount: e.target.checked ? 10 : null
                    });
                  }}
                  color="warning"
                />
              }
              label="Offer discount for first-time users"
              sx={{ mt: 2 }}
            />

            {newPlan.first_time_discount !== null && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Discount Percentage</InputLabel>
                <Select
                  value={newPlan.first_time_discount}
                  onChange={(e) => setNewPlan({ ...newPlan, first_time_discount: e.target.value })}
                  label="Discount Percentage"
                >
                  {DISCOUNT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={newPlan.recurring}
                  onChange={(e) => setNewPlan({ ...newPlan, recurring: e.target.checked })}
                />
              }
              label="Recurring Plan"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreatePlanOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreatePlan}
            variant="contained"
            size="large"
            disabled={!newPlan.plan_type || !newPlan.price_cents}
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
            }}
          >
            Create Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={editPlanOpen} onClose={() => setEditPlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">Edit Plan</Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {selectedPlan?.name}
              </Typography>
              <Typography variant="body2">
                {selectedPlan?.description}
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Price (₹)"
              type="number"
              value={editPlanData.price_cents}
              onChange={(e) => setEditPlanData({ ...editPlanData, price_cents: e.target.value })}
              margin="normal"
              required
              helperText="Enter amount in rupees"
              inputProps={{ min: '0' }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Daily Hours Limit</InputLabel>
              <Select
                value={editPlanData.hours_per_day === null ? 'unlimited' : editPlanData.hours_per_day}
                onChange={(e) => {
                  const value = e.target.value === 'unlimited' ? null : e.target.value;
                  setEditPlanData({ ...editPlanData, hours_per_day: value });
                }}
                label="Daily Hours Limit"
              >
                {HOURS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'unlimited'} value={option.value === null ? 'unlimited' : option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={editPlanData.first_time_discount !== null}
                  onChange={(e) => {
                    setEditPlanData({
                      ...editPlanData,
                      first_time_discount: e.target.checked ? 10 : null
                    });
                  }}
                  color="warning"
                />
              }
              label="Offer discount for first-time users"
              sx={{ mt: 2 }}
            />

            {editPlanData.first_time_discount !== null && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Discount Percentage</InputLabel>
                <Select
                  value={editPlanData.first_time_discount}
                  onChange={(e) => setEditPlanData({ ...editPlanData, first_time_discount: e.target.value })}
                  label="Discount Percentage"
                >
                  {DISCOUNT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={editPlanData.recurring}
                  onChange={(e) => setEditPlanData({ ...editPlanData, recurring: e.target.checked })}
                />
              }
              label="Recurring Plan"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditPlanOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditPlan}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
            }}
          >
            Update Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Plan Dialog */}
      <Dialog open={deletePlanOpen} onClose={() => setDeletePlanOpen(false)} maxWidth="sm">
        <DialogTitle>
          <Typography variant="h5" fontWeight="700" color="error">
            Delete Plan?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedPlan?.name}</strong>? This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Plans with active bookings cannot be deleted.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeletePlanOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeletePlan}
            variant="contained"
            color="error"
            size="large"
            sx={{ borderRadius: 3 }}
          >
            Delete Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Librarian Dialog */}
      <Dialog open={createLibrarianOpen} onClose={() => setCreateLibrarianOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Librarian</DialogTitle>
        <DialogContent>
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Name"
              value={newLibrarian.name}
              onChange={(e) => setNewLibrarian({ ...newLibrarian, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newLibrarian.email}
              onChange={(e) => setNewLibrarian({ ...newLibrarian, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={newLibrarian.password}
              onChange={(e) => setNewLibrarian({ ...newLibrarian, password: e.target.value })}
              required
              fullWidth
              helperText="Minimum 6 characters"
            />
            <TextField
              label="Phone Number"
              value={newLibrarian.phone}
              onChange={(e) => setNewLibrarian({ ...newLibrarian, phone: e.target.value })}
              fullWidth
            />
            <Autocomplete
              options={librarySearchOptions.filter(lib => !lib.librarian_id)}
              getOptionLabel={(option) => option.name || ''}
              value={librarySearchOptions.find(lib => lib.id === newLibrarian.library_id) || null}
              onChange={(event, newValue) => {
                setNewLibrarian({ ...newLibrarian, library_id: newValue?.id || '' });
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  searchLibrariesForAssignment(newInputValue);
                }
              }}
              onOpen={() => searchLibrariesForAssignment('')}
              loading={librarySearchLoading}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography fontWeight="600">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.address?.substring(0, 40)}...
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign to Library (Optional)"
                  placeholder="Search library..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {librarySearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateLibrarianOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateLibrarian}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #F7B32B 0%, #C88A00 100%)',
              borderRadius: 3
            }}
          >
            Create Librarian
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Librarian Dialog */}
      <Dialog
        open={editLibrarianOpen}
        onClose={() => {
          setEditLibrarianOpen(false);
          setSelectedLibrarian(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Librarian</DialogTitle>
        <DialogContent>
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Name"
              value={editLibrarianData.name}
              onChange={(e) => setEditLibrarianData({ ...editLibrarianData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              value={editLibrarianData.email}
              disabled
              fullWidth
              helperText="Email cannot be changed"
            />
            <TextField
              label="Phone Number"
              value={editLibrarianData.phone}
              onChange={(e) => setEditLibrarianData({ ...editLibrarianData, phone: e.target.value })}
              fullWidth
              placeholder="+91 9876543210"
              helperText="WhatsApp number for booking notifications"
            />
            <Autocomplete
              options={INDIAN_CITIES}
              value={editLibrarianData.city || null}
              onChange={(event, newValue) => {
                setEditLibrarianData({ ...editLibrarianData, city: newValue || '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                />
              )}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            setEditLibrarianOpen(false);
            setSelectedLibrarian(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateLibrarian}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #F7B32B 0%, #C88A00 100%)',
              borderRadius: 3
            }}
          >
            Update Librarian
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Librarian Dialog */}
      <Dialog open={assignLibrarianOpen} onClose={() => setAssignLibrarianOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Library</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Assigning librarian: <strong>{selectedLibrarian?.name}</strong>
            </Typography>
            <Autocomplete
              options={librarySearchOptions}
              getOptionLabel={(option) => option.name || ''}
              value={selectedAssignLibrary}
              onChange={(event, newValue) => {
                setSelectedAssignLibrary(newValue);
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  searchLibrariesForAssignment(newInputValue);
                }
              }}
              loading={librarySearchLoading}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography fontWeight="600">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.address?.substring(0, 50)}{option.address?.length > 50 ? '...' : ''}
                    </Typography>
                    {option.librarian_id && option.librarian_id !== selectedLibrarian?.id && (
                      <Chip label="Already assigned" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Library"
                  placeholder="Type to search..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {librarySearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
            />
            <Box mt={2} display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => {
                  handleAssignLibrarian(selectedLibrarian.id, selectedAssignLibrary?.id || null);
                }}
              >
                {selectedAssignLibrary ? 'Assign Library' : 'Unassign Library'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignLibrarianOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Institute Dialog */}
      <Dialog open={createInstituteOpen} onClose={() => setCreateInstituteOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">Create New Institute</Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
              Institute Information
            </Typography>
            <TextField
              fullWidth
              label="Institute Name"
              value={newInstitute.name}
              onChange={(e) => setNewInstitute({ ...newInstitute, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Address"
              value={newInstitute.address}
              onChange={(e) => setNewInstitute({ ...newInstitute, address: e.target.value })}
              margin="normal"
              required
              multiline
              rows={2}
            />
            <Autocomplete
              options={INDIAN_CITIES}
              value={newInstitute.city}
              onChange={(event, newValue) => setNewInstitute({ ...newInstitute, city: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  margin="normal"
                />
              )}
              fullWidth
            />
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={newInstitute.contact_email}
              onChange={(e) => setNewInstitute({ ...newInstitute, contact_email: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Established Year"
              type="number"
              value={newInstitute.established_year}
              onChange={(e) => setNewInstitute({ ...newInstitute, established_year: e.target.value })}
              margin="normal"
              inputProps={{ min: 1900, max: 2100 }}
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
              Optional: Create Manager Simultaneously
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              You can optionally create a manager for this institute in one step
            </Alert>
            <TextField
              fullWidth
              label="Manager Name"
              value={newInstitute.manager_name}
              onChange={(e) => setNewInstitute({ ...newInstitute, manager_name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Manager Email"
              type="email"
              value={newInstitute.manager_email}
              onChange={(e) => setNewInstitute({ ...newInstitute, manager_email: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Manager Password"
              type="password"
              value={newInstitute.manager_password}
              onChange={(e) => setNewInstitute({ ...newInstitute, manager_password: e.target.value })}
              margin="normal"
              helperText="Leave blank if not creating manager now"
            />
            <TextField
              fullWidth
              label="Manager Phone"
              value={newInstitute.manager_phone}
              onChange={(e) => setNewInstitute({ ...newInstitute, manager_phone: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateInstituteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateInstitute}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
            }}
          >
            Create Institute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Institute Dialog */}
      <Dialog open={editInstituteOpen} onClose={() => setEditInstituteOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">Edit Institute</Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              fullWidth
              label="Institute Name"
              value={editInstituteData.name}
              onChange={(e) => setEditInstituteData({ ...editInstituteData, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Address"
              value={editInstituteData.address}
              onChange={(e) => setEditInstituteData({ ...editInstituteData, address: e.target.value })}
              margin="normal"
              multiline
              rows={2}
            />
            <Autocomplete
              options={INDIAN_CITIES}
              value={editInstituteData.city}
              onChange={(event, newValue) => setEditInstituteData({ ...editInstituteData, city: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  margin="normal"
                />
              )}
              fullWidth
            />
            <Box display="flex" gap={2} mt={1}>
              <TextField
                fullWidth
                label="Latitude"
                value={editInstituteData.lat}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, lat: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Longitude"
                value={editInstituteData.lng}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, lng: e.target.value })}
                margin="normal"
              />
            </Box>
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={editInstituteData.contact_email}
              onChange={(e) => setEditInstituteData({ ...editInstituteData, contact_email: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Established Year"
              type="number"
              value={editInstituteData.established_year}
              onChange={(e) => setEditInstituteData({ ...editInstituteData, established_year: e.target.value })}
              margin="normal"
              inputProps={{ min: 1900, max: 2100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditInstituteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditInstitute}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
            }}
          >
            Update Institute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Institute Dialog */}
      <Dialog open={deleteInstituteOpen} onClose={() => setDeleteInstituteOpen(false)} maxWidth="sm">
        <DialogTitle>
          <Typography variant="h5" fontWeight="700" color="error">
            Delete Institute?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedInstitute?.name}</strong>? This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Institutes with active managers or students cannot be deleted.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteInstituteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteInstitute}
            variant="contained"
            color="error"
            size="large"
            sx={{ borderRadius: 3 }}
          >
            Delete Institute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Institute Manager Dialog */}
      <Dialog open={createInstituteManagerOpen} onClose={() => setCreateInstituteManagerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">Create New Institute Manager</Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              fullWidth
              label="Manager Name"
              value={newInstituteManager.name}
              onChange={(e) => setNewInstituteManager({ ...newInstituteManager, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newInstituteManager.email}
              onChange={(e) => setNewInstituteManager({ ...newInstituteManager, email: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newInstituteManager.password}
              onChange={(e) => setNewInstituteManager({ ...newInstituteManager, password: e.target.value })}
              margin="normal"
              required
              helperText="Minimum 6 characters"
            />
            <TextField
              fullWidth
              label="Phone"
              value={newInstituteManager.phone}
              onChange={(e) => setNewInstituteManager({ ...newInstituteManager, phone: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="City"
              value={newInstituteManager.city}
              onChange={(e) => setNewInstituteManager({ ...newInstituteManager, city: e.target.value })}
              margin="normal"
            />
            <TextField
              select
              fullWidth
              label="Assign to Institute (optional)"
              value={newInstituteManager.institute_id}
              onChange={(e) => setNewInstituteManager({ ...newInstituteManager, institute_id: e.target.value })}
              margin="normal"
            >
              <MenuItem value="">None</MenuItem>
              {institutes.map((inst) => (
                <MenuItem key={inst.id} value={inst.id}>
                  {inst.name} {inst.manager_id ? '(Already has manager)' : ''}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateInstituteManagerOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateInstituteManager}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)'
            }}
          >
            Create Manager
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Institute Manager Dialog */}
      <Dialog open={assignInstituteManagerOpen} onClose={() => setAssignInstituteManagerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Institute</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Assigning manager: <strong>{selectedInstituteManager?.name}</strong>
            </Typography>
            <TextField
              select
              label="Select Institute"
              fullWidth
              defaultValue={selectedInstituteManager?.institute?.id || ''}
              onChange={(e) => {
                const instituteId = e.target.value;
                handleAssignInstituteManager(selectedInstituteManager.id, instituteId);
              }}
            >
              <MenuItem value="">None (Unassign)</MenuItem>
              {institutes.map((inst) => (
                <MenuItem key={inst.id} value={inst.id}>
                  {inst.name} {inst.manager_id && inst.manager_id !== selectedInstituteManager?.id ? '(Already has manager)' : ''}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignInstituteManagerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Student Form Dialog - Create */}
      <StudentFormDialog
        open={createStudentOpen}
        onClose={() => setCreateStudentOpen(false)}
        student={null}
        onSuccess={() => {
          fetchStudents();
          fetchDashboardData();
        }}
        showSnackbar={showSnackbar}
        apiPrefix="/admin"
      />

      {/* Student Form Dialog - Edit */}
      <StudentFormDialog
        open={editStudentOpen}
        onClose={() => {
          setEditStudentOpen(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
        onSuccess={() => {
          fetchStudents();
          setEditStudentOpen(false);
          setSelectedStudent(null);
        }}
        showSnackbar={showSnackbar}
        apiPrefix="/admin"
      />

      {/* Password Reset Dialog */}
      <Dialog
        open={resetPasswordOpen}
        onClose={() => {
          setResetPasswordOpen(false);
          setResetPasswordUser(null);
          setNewPassword('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LockReset color="warning" />
            <Typography variant="h6" fontWeight="700">Reset Password</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            {resetPasswordUser && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Resetting password for <strong>{resetPasswordUser.name}</strong> ({resetPasswordUser.email})
              </Alert>
            )}
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 characters)"
              helperText="Password must be at least 6 characters"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => {
              setResetPasswordOpen(false);
              setResetPasswordUser(null);
              setNewPassword('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetPassword}
            disabled={resetPasswordLoading || newPassword.length < 6}
            startIcon={resetPasswordLoading ? <CircularProgress size={16} /> : <LockReset />}
          >
            {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminDashboardPage;
